// contests.component.ts
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, OnDestroy, ViewChild, TemplateRef, HostListener, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, NavigationStart } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { DataService } from '@services/contests-data.service';
import { SharedModule } from '@shared/shared.module';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HighlightPipe } from "../../../pipes/highlight.pipe";
import { v4 as uuidv4 } from 'uuid';
import { FilterModalComponent, FilterConfig } from '@pages/filter-modal/filter-modal.component';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SecureStorageService } from '@services/secure-storage.service';
import { isArabicLang, reshapeArabic } from 'src/app/utils/arabic-pdf.util';
import { translateCountryName } from 'src/app/utils/country-i18n.util';

interface OverviewStats {
  totalContests: number;
  activeContests: number;
  pendingContests: number;
  finishedContests: number;
  avgClickRate: number;
  avgCompletionRate: number;
  avgParticipationRate: number;
  avgEngagementRate: number;
}

@Component({
  selector: 'app-contests',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    SharedModule,
    NzPaginationModule,
    NzSelectModule,
    NzModalModule,
    NzTableModule,
    NzToolTipModule,
    NzPopconfirmModule,
    FilterModalComponent,
    HighlightPipe,
    NzDatePickerModule
  ],
  templateUrl: './contests.component.html',
  styleUrls: ['./contest.component.scss'],
})
export class ContestsComponent implements OnInit, OnDestroy {
  private routerSubscription!: Subscription;
  contest: any[] = [];
  completedPageIndex = 1;
  draftPageIndex = 1;
  filterModalVisible: boolean = false;
  pageIndex = 1;
  perPage = 5;
  totalContestLength = 0;
  totalCompletedContestLength = 0;
  totalDraftContestLength = 0;
  searchTerm: string = '';
  countryRecords: any;
  currentFilters: any = {};
  activeShareMenuIndex: number | null = null;
  activeShareId: number | null = null;
  tabs_grid: 'all_contest' | 'completed' | 'draft' = 'all_contest';
  completedContests: any[] = [];
  draftContests: any[] = [];
  // Overview variables
  overviewStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  previousStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  overviewTrends: Record<keyof OverviewStats | 'overallSuccessRate', number> = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0,
    overallSuccessRate: 0
  };
  overviewLoading: boolean = false;
  overviewDateRange: Date[] = [];
  overviewPickerOpen: boolean = false;
  private elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.overviewPickerOpen) {
      const target = event.target as HTMLElement;
      const isInsideDateArea = target.closest('.btn-date')?.parentElement
        || target.closest('.ant-picker-dropdown')
        || target.closest('.ant-picker-panel')
        || target.closest('nz-range-picker');
      if (!isInsideDateArea) {
        this.overviewPickerOpen = false;
      }
    }
  }
  overviewContestId: string = '';
  contestsList: any[] = [];
  http = inject(HttpClient);
  pagesService = inject(PagesService);
  fb = inject(FormBuilder);
  sharedService = inject(SharedService);
  secureStorage = inject(SecureStorageService);
  dataService = inject(DataService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;





  // Filter configuration for modal
  filterConfig: FilterConfig = {
    showTimeline: true,
    showLevel: true,
    showStatus: true,
    showFormat: true,
    showType: true,
    showViewType: true,
    showLocation: true,
    statusOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'ongoing', label: 'Ongoing' },
      { value: 'waiting_prize', label: 'Waiting Prize' },
      { value: 'completed', label: 'Completed' },
      { value: 'draft', label: 'Draft' }
    ],
    levelOptions: [1, 2, 3, 4, 5],
    formatOptions: [
      { value: 'integrated', label: 'Integrated' },
      { value: 'non-integrated', label: 'Non-Integrated' }
    ],
    typeOptions: [
      { value: 'private', label: 'Private' },
      { value: 'public', label: 'Public' }
    ],
    viewTypeOptions: [
      { value: 'vip', label: 'VIP' },
      { value: 'recent contest', label: 'Recent Engagement' }
    ]
  };

  // Image Modal Properties
  imageModalVisible: boolean = false;
  imageModalImages: string[] = [];
  imageModalCurrentIndex: number = 0;

  // Soft Delete with Undo Properties
  pendingDeletes: Map<number, { contest: any, timer: any, listType: string, pageIndex: number }> = new Map();
  @ViewChild('undoNotificationTpl', { static: true }) undoNotificationTpl!: TemplateRef<any>;
  hasSdkAccess = false;

  constructor(private modal: NzModalService, private notificationService: NzNotificationService) { }

  ngOnInit(): void {
    this.hasSdkAccess = this.secureStorage.getItem<boolean>('hasSdkAccess') || false;
    this.getContestLists();
    this.getCountryRecords();
    this.getOverviewStats();
    this.loadContestsList();

    // Listen for route changes to delete pending items before navigation
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.deletePendingContestsImmediately();
      });
  }

  ngOnDestroy(): void {
    // Cleanup router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    // Delete any pending contests before component is destroyed
    this.deletePendingContestsImmediately();
  }

  // Delete all pending contests immediately (on route change or component destroy)
  private deletePendingContestsImmediately(): void {
    this.pendingDeletes.forEach((_, id) => {
      // Call API to delete
      const data = { contest_id: id };
      this.pagesService.update('DELETE_CONTEST', data).subscribe({
        next: () => {
          this.dataService.refreshContestData();
        },
        error: () => { }
      });
    });
    this.pendingDeletes.clear();
    this.notificationService.remove();
  }

  private buildContestListRequest(page: number, perPage: number, status: string = '', filters: any = {}): any {
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      page,
      per_page: perPage,
      status: status || '',
      format_of_contest: filters.format || '',
      type: filters.type || '',
      View_Type: filters.viewType || '',
      location: (filters.location && Array.isArray(filters.location) && filters.location.length > 0)
        ? filters.location.join(',')
        : '',
      level_expiry_start_date: (filters.dateRange && filters.dateRange.length === 2)
        ? formatDate(new Date(filters.dateRange[0]))
        : '',
      level_expiry_end_date: (filters.dateRange && filters.dateRange.length === 2)
        ? formatDate(new Date(filters.dateRange[1]))
        : '',
      number_of_levels: filters.level ? filters.level.toString() : '',
      search: this.searchTerm?.trim() || ''
    };
  }

  private getExportConfigByTab(): { status: string; reportTitle: string; fileName: string } {
    const isAr = isArabicLang();
    switch (this.tabs_grid) {
      case 'completed':
        return {
          status: 'completed',
          reportTitle: isAr ? 'تقرير التفاعلات المكتملة' : 'Completed Engagements Report',
          fileName: isAr ? 'تقرير_التفاعلات_المكتملة' : 'Completed_Engagements_Report'
        };
      case 'draft':
        return {
          status: 'draft',
          reportTitle: isAr ? 'تقرير تفاعلات المسودة' : 'Draft Engagements Report',
          fileName: isAr ? 'تقرير_تفاعلات_المسودة' : 'Draft_Engagements_Report'
        };
      default:
        return {
          status: '',
          reportTitle: isAr ? 'تقرير جميع التفاعلات' : 'All Engagements Report',
          fileName: isAr ? 'تقرير_جميع_التفاعلات' : 'All_Engagements_Report'
        };
    }
  }

  private fetchAllContestsForExport(
    status: string,
    onSuccess: (items: any[]) => void
  ): void {
    const totalCount = this.getActiveTotalCount();
    const finalStatus = this.currentFilters?.status || status || '';

    if (!totalCount || totalCount <= 0) {
      this.sharedService.createNotification('error', 'No Engagements Found');
      return;
    }

    const requestBody = this.buildContestListRequest(1, totalCount, finalStatus, this.currentFilters || {});
    this.sharedService.showLoader();
    this.pagesService.getAll('TEST_PAGINATION', requestBody).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        const contestsToExport = response?.items || [];
        if (!Array.isArray(contestsToExport) || contestsToExport.length === 0) {
          this.sharedService.createNotification('error', 'No Engagements Found');
          return;
        }
        onSuccess(contestsToExport);
      },
      error: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to fetch engagements for export');
      }
    });
  }

  getContestLists(page: number = 1, status: string = '', filters: any = {}, showLoader: boolean = true) {
    if (showLoader) this.sharedService.showLoader();
    const now = new Date().getTime();

    const finalStatus = filters.status || status || '';
    const requestBody: any = this.buildContestListRequest(page, this.perPage, finalStatus, filters);

    this.pagesService.getAll('TEST_PAGINATION', requestBody).subscribe({
      next: (response: any) => {
        // Extract items and meta from response
        const items = response?.items || [];
        const meta = response?.meta || {};

        // Process contest items
        const processedContests = items.map((contest: any) => {
          // ✅ Clean image URLs
          if (Array.isArray(contest.banners)) {
            contest.banners = contest.banners.map((banner: any) => ({
              ...banner,
              url: this.cleanImageUrl(banner.url || banner.path || ''),
              path: this.cleanImageUrl(banner.path || banner.url || '')
            }));
          }

          if (Array.isArray(contest._contest_prize_of_contest_list)) {
            contest._contest_prize_of_contest_list = contest._contest_prize_of_contest_list.map((prize: any) => ({
              ...prize,
              image: prize.image
                ? {
                  ...prize.image,
                  url: this.cleanImageUrl(prize.image.url || prize.image.path || ''),
                  path: this.cleanImageUrl(prize.image.path || prize.image.url || '')
                }
                : null
            }));
          }

          // ✅ Mark expired
          contest.isExpired = contest.end_date
            ? new Date(contest.end_date).getTime() < now
            : false;

          return contest;
        });

        // Sort by created_at descending (newest first)
        processedContests.sort((a: any, b: any) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        });

        // Use filtered counts from backend for pagination
        // When filters are applied, use filtered_total/filtered_draft/filtered_completed
        // When no filters, use all_count/draft_count/completed_count

        if (this.tabs_grid === 'all_contest') {
          this.contest = processedContests;
          // Use filtered_total if available, otherwise fall back to all_count
          this.totalContestLength = meta?.filtered_total ?? meta?.all_count ?? items.length;
        } else if (this.tabs_grid === 'completed') {
          this.completedContests = processedContests;
          this.totalCompletedContestLength = meta?.filtered_completed ?? meta?.completed_count ?? items.length;
        } else if (this.tabs_grid === 'draft') {
          this.draftContests = processedContests;
          this.totalDraftContestLength = meta?.filtered_draft ?? meta?.draft_count ?? items.length;
        }

        if (showLoader) this.sharedService.hideLoader();
      },
      error: (error) => {
        if (showLoader) this.sharedService.hideLoader();
        this.sharedService.createNotification('error', error?.error?.message || 'Failed to load engagements');
      },
    });
  }

  private cleanImageUrl(url: string): string {
    return this.sharedService.cleanImageUrl(url);
  }

  // Get overview stats from API
  getOverviewStats(): void {
    const params: any = {
      contestid: this.overviewContestId || 0,
      contest_id: this.overviewContestId || 0,
      start_date: this.overviewDateRange[0] ? this.formatDateToString(this.overviewDateRange[0]) : 0,
      end_date: this.overviewDateRange[1] ? this.formatDateToString(this.overviewDateRange[1]) : 0
    };

    this.overviewLoading = true;

    this.pagesService.getAll('DASH_CONTEST_OVERVIEW', params).subscribe({
      next: (res: any) => {
        const readValue = (obj: any, fallback: number = 0): number => {
          if (obj == null) return fallback;
          if (typeof obj === 'number') return obj;
          if (typeof obj?.value === 'number') return obj.value;
          return fallback;
        };
        const readTrend = (obj: any): number | null => {
          if (obj == null) return null;
          if (typeof obj?.trend === 'number') return obj.trend;
          return null;
        };
        const prevFromTrend = (current: number, trend: number | null): number => {
          if (trend == null || !isFinite(trend)) return 0;
          const denom = 1 + trend / 100;
          if (denom === 0) return 0;
          return current / denom;
        };

        const totalContests = readValue(res?.totalContests, readValue(res?.total_contest, 0));
        const activeContests = readValue(res?.activeContests, readValue(res?.active_contest, 0));
        const pendingContests = readValue(res?.pendingContests, readValue(res?.pending_contest, 0));
        const finishedContests = readValue(res?.finishedContests, readValue(res?.finished_contest, 0));
        const overallSuccessRate = readValue(
          res?.overallSuccessRate,
          readValue(res?.averageClickThroughRate, readValue(res?.averageCTR, 0))
        );
        const avgCompletion = readValue(
          res?.avgCompletionRate,
          readValue(res?.averageCompletionRate, readValue(res?.avg_completion_rate, 0))
        );
        const avgParticipation = readValue(
          res?.avgParticipationRate,
          readValue(res?.averageParticipationRate, readValue(res?.avg_participation_rate, 0))
        );
        const avgEngagement = readValue(
          res?.avgEngagementRate,
          readValue(res?.averageEngagementRate, readValue(res?.avg_engagement_rate, 0))
        );

        const totalTrend = readTrend(res?.totalContests);
        const activeTrend = readTrend(res?.activeContests);
        const pendingTrend = readTrend(res?.pendingContests);
        const finishedTrend = readTrend(res?.finishedContests);
        const overallTrend = readTrend(res?.overallSuccessRate) ?? readTrend(res?.averageClickThroughRate);
        const completionTrend = readTrend(res?.avgCompletionRate) ?? readTrend(res?.averageCompletionRate);
        const participationTrend = readTrend(res?.avgParticipationRate) ?? readTrend(res?.averageParticipationRate);
        const engagementTrend = readTrend(res?.avgEngagementRate) ?? readTrend(res?.averageEngagementRate);

        this.overviewStats = {
          totalContests,
          activeContests,
          pendingContests,
          finishedContests,
          avgClickRate: overallSuccessRate,
          avgCompletionRate: avgCompletion,
          avgParticipationRate: avgParticipation,
          avgEngagementRate: avgEngagement
        };

        this.previousStats = {
          totalContests: prevFromTrend(totalContests, totalTrend),
          activeContests: prevFromTrend(activeContests, activeTrend),
          pendingContests: prevFromTrend(pendingContests, pendingTrend),
          finishedContests: prevFromTrend(finishedContests, finishedTrend),
          avgClickRate: prevFromTrend(overallSuccessRate, overallTrend),
          avgCompletionRate: prevFromTrend(avgCompletion, completionTrend),
          avgParticipationRate: prevFromTrend(avgParticipation, participationTrend),
          avgEngagementRate: prevFromTrend(avgEngagement, engagementTrend)
        };

        this.overviewTrends = {
          totalContests: totalTrend ?? 0,
          activeContests: activeTrend ?? 0,
          pendingContests: pendingTrend ?? 0,
          finishedContests: finishedTrend ?? 0,
          avgClickRate: overallTrend ?? 0,
          avgCompletionRate: completionTrend ?? 0,
          avgParticipationRate: participationTrend ?? 0,
          avgEngagementRate: engagementTrend ?? 0,
          overallSuccessRate: overallTrend ?? 0
        };
        this.overviewLoading = false;
      },
      error: (error) => {
        console.error('Failed to load overview stats:', error);
        this.overviewLoading = false;
      }
    });
  }

  // Open filter modal
  openFilterModal(): void {
    this.filterModalVisible = true;
  }
  // Handle filters applied from modal
  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    // Reset to page 1 when filters change
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API with filters
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed', filters);
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft', filters);
    } else {
      this.getContestLists(1, '', filters);
    }
  }
  // Handle filter reset from modal
  onFiltersReset(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    // Reset to page 1
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API without filters
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed');
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft');
    } else {
      this.getContestLists(1, '');
    }
  }

  // Helper method to check if any filters are active
  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }
  // Apply search (called on search input change) - no loader
  applySearch(): void {
    // Reset to page 1 when search changes
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API with current filters + search term (no loader for search)
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed', this.currentFilters, false);
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft', this.currentFilters, false);
    } else {
      this.getContestLists(1, '', this.currentFilters, false);
    }
  }
  // Clear all filters and reset
  clearAllFilters(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    // Reset the filter modal form
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        timeline: null,
        level: null,
        status: null,
        format: null,
        type: null,
        viewType: null,
        location: []
      });
    }
    // Call API without filters
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed');
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft');
    } else {
      this.getContestLists(1, '');
    }
  }

  // Helper: Format Date to DD-MM-YYYY string
  formatDateToString(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  loadContestsList(): void {
    this.pagesService.getAll('GET_DASHBOARD_CONTESTS', { contest_id: 0 }).subscribe({
      next: (response: any) => {
        this.contestsList = response?.items || response?.data || response || [];
      },
      error: () => {
        this.contestsList = [];
      }
    });
  }

  onOverviewContestChange(contestId: string): void {
    this.overviewContestId = contestId || '';
    this.getOverviewStats();
  }

  onOverviewDateChange(dates: Date[]): void {
    this.overviewDateRange = dates || [];
    this.getOverviewStats();
  }
  getTimeCategory(date: string): 'past' | 'ongoing' | 'upcoming' | 'expired' {
    const now = new Date();
    const endDate = new Date(date);

    if (isNaN(endDate.getTime())) return 'expired';
    if (endDate.getTime() < now.getTime()) return 'expired';

    const diffHours = Math.abs(endDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (diffHours <= 12) return 'ongoing';

    return 'upcoming';
  }
  get pendingContests() {
    return this.contest.filter(
      (x) => (x.approval_status?.toLowerCase() ?? '') === 'pending'
    );
  }
  get pagedAllContests() {
    // Server-side pagination: return all items as backend already paginated
    return this.contest || [];
  }

  get pagedDraftContests() {
    // Server-side pagination: return all items as backend already paginated
    return this.draftContests || [];
  }
  getPageRangeText(list: any[], pageIndex: number): string {
    // For server-side pagination, calculate based on per_page and current page
    let total = 0;
    if (this.tabs_grid === 'completed') {
      total = this.totalCompletedContestLength;
    } else if (this.tabs_grid === 'draft') {
      total = this.totalDraftContestLength;
    } else {
      total = this.totalContestLength;
    }

    const start = total === 0 ? 0 : (pageIndex - 1) * this.perPage + 1;
    const end = Math.min(pageIndex * this.perPage, total);
    return `${start}-${end} of ${total} items`;
  }

  getPageRangeTextTotal(total: number, pageIndex: number): string {
    const start = total === 0 ? 0 : (pageIndex - 1) * this.perPage + 1;
    const end = Math.min(pageIndex * this.perPage, total);
    return `${start}-${end} of ${total} items`;
  }
  editContest(id: number) {
    const uid = uuidv4();
    localStorage.setItem(uid, id.toString());
    this.router.navigate(['/home/contests/edit'], { queryParams: { id: uid } });
  }
  // Soft delete with undo functionality
  softDeleteContest(id: number) {
    // Find the contest in the appropriate list
    let contest: any = null;
    let listType = '';

    if (this.tabs_grid === 'all_contest') {
      contest = this.contest.find(c => c.id === id);
      listType = 'all';
    } else if (this.tabs_grid === 'completed') {
      contest = this.completedContests.find(c => c.id === id);
      listType = 'completed';
    } else if (this.tabs_grid === 'draft') {
      contest = this.draftContests.find(c => c.id === id);
      listType = 'draft';
    }

    if (!contest) return;

    // Store current page index before deletion
    let currentPageIndex = this.pageIndex;
    if (listType === 'completed') {
      currentPageIndex = this.completedPageIndex;
    } else if (listType === 'draft') {
      currentPageIndex = this.draftPageIndex;
    }

    // Remove from display list immediately (just visually hide it)
    if (listType === 'all') {
      this.contest = this.contest.filter(c => c.id !== id);
      this.totalContestLength--;
    } else if (listType === 'completed') {
      this.completedContests = this.completedContests.filter(c => c.id !== id);
      this.totalCompletedContestLength--;
    } else if (listType === 'draft') {
      this.draftContests = this.draftContests.filter(c => c.id !== id);
      this.totalDraftContestLength--;
    }

    // Store pending delete info with page index for undo
    this.pendingDeletes.set(id, { contest, timer: null, listType, pageIndex: currentPageIndex });

    // Show notification with undo button
    const notificationRef = this.notificationService.template(this.undoNotificationTpl, {
      nzData: { id, contestName: contest.name },
      nzDuration: 3000,
      nzPlacement: 'topRight',
      nzClass: 'undo-notification',
      nzKey: `delete-${id}`,
      nzPauseOnHover: true
    });

    // When notification closes (timeout or manually), trigger permanent delete
    notificationRef.onClose.subscribe(() => {
      // Small delay to allow undo click to process first
      setTimeout(() => {
        if (this.pendingDeletes.has(id)) {
          this.permanentDeleteContest(id);
        }
      }, 100);
    });
  }

  // Undo delete - restore contest to list
  undoDelete(id: number) {
    const pendingDelete = this.pendingDeletes.get(id);
    if (!pendingDelete) return;

    // Remove from pending deletes first to prevent double processing
    this.pendingDeletes.delete(id);

    // Close specific notification
    this.notificationService.remove(`delete-${id}`);

    // Restore to the appropriate list and refresh with correct page
    if (pendingDelete.listType === 'all') {
      this.totalContestLength++;
      this.pageIndex = pendingDelete.pageIndex;
      this.getContestLists(this.pageIndex, '', this.currentFilters, false);
    } else if (pendingDelete.listType === 'completed') {
      this.totalCompletedContestLength++;
      this.completedPageIndex = pendingDelete.pageIndex;
      this.getContestLists(this.completedPageIndex, 'completed', this.currentFilters, false);
    } else if (pendingDelete.listType === 'draft') {
      this.totalDraftContestLength++;
      this.draftPageIndex = pendingDelete.pageIndex;
      this.getContestLists(this.draftPageIndex, 'draft', this.currentFilters, false);
    }

    this.sharedService.createNotification('success', 'Engagement restored successfully!');
  }

  // Permanent delete - called after 10 seconds if not undone
  permanentDeleteContest(id: number) {
    const pendingDelete = this.pendingDeletes.get(id);
    if (!pendingDelete) return;

    // Remove from pending deletes
    this.pendingDeletes.delete(id);

    // Call API to delete from database
    const data = { contest_id: id };
    this.pagesService.update('DELETE_CONTEST', data).subscribe({
      next: () => {
        this.getOverviewStats();
        this.dataService.refreshContestData();

        // Refresh list to get correct number of records per page
        if (pendingDelete.listType === 'all') {
          // Check if we need to go back a page (if current page is now empty)
          const maxPage = Math.ceil(this.totalContestLength / this.perPage) || 1;
          if (this.pageIndex > maxPage) {
            this.pageIndex = maxPage;
          }
          this.getContestLists(this.pageIndex, '', this.currentFilters, false);
        } else if (pendingDelete.listType === 'completed') {
          const maxPage = Math.ceil(this.totalCompletedContestLength / this.perPage) || 1;
          if (this.completedPageIndex > maxPage) {
            this.completedPageIndex = maxPage;
          }
          this.getContestLists(this.completedPageIndex, 'completed', this.currentFilters, false);
        } else if (pendingDelete.listType === 'draft') {
          const maxPage = Math.ceil(this.totalDraftContestLength / this.perPage) || 1;
          if (this.draftPageIndex > maxPage) {
            this.draftPageIndex = maxPage;
          }
          this.getContestLists(this.draftPageIndex, 'draft', this.currentFilters, false);
        }
      },
      error: () => {
        // If delete fails, restore by refreshing the list
        if (pendingDelete.listType === 'all') {
          this.totalContestLength++;
          this.getContestLists(pendingDelete.pageIndex, '', this.currentFilters, false);
        } else if (pendingDelete.listType === 'completed') {
          this.totalCompletedContestLength++;
          this.getContestLists(pendingDelete.pageIndex, 'completed', this.currentFilters, false);
        } else if (pendingDelete.listType === 'draft') {
          this.totalDraftContestLength++;
          this.getContestLists(pendingDelete.pageIndex, 'draft', this.currentFilters, false);
        }
        this.sharedService.createNotification('error', 'Failed to delete engagement');
      },
    });
  }

  // Legacy delete method (keeping for reference)
  deleteContest(id: number) {
    this.sharedService.showLoader();
    const data = { contest_id: id };
    this.pagesService.update('DELETE_CONTEST', data).subscribe({
      next: (response) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('success', 'Engagement deleted successfully!');
        this.getContestLists();
        this.getOverviewStats();
        this.dataService.refreshContestData();
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(error, error);
      },
    });
  }
  showContestDetail(contest: any) {
    const { id } = contest;
    const uid = uuidv4();
    localStorage.setItem(uid, id.toString());
    this.router.navigate(['/home/contests/detail'], { queryParams: { id: uid } });
  }

  showParticipantDetail(item: any) {
    const contestId = item?.id; // item me se contest ki ID nikal lo
    if (contestId) {
      this.router.navigate(['/home/participant'], {
        queryParams: { id: contestId } // id ko param me pass kar diya
      });
    } else {
      console.error('Contest ID not found!');
    }
  }

  // Opens the Web QR instances page for a live contest.
  openWebQr(contest: any) {
    if (!contest?.id) {
      return;
    }
    this.router.navigate(['/home/contests/web-qr'], {
      queryParams: { contest_id: contest.id, contest_name: contest.name || '' }
    });
  }

  viewParticipants(contestId: number, contestName?: string) {
    if (contestId) {
      this.router.navigate(['/home/participants'], {
        queryParams: { contest_id: contestId, contest_name: contestName || '' }
      });
    }
  }

  getTimeRemaining(createdAt: string) {
    return this.sharedService.timeRemaining(createdAt);
  }
  onPageSizeChange(size: number): void {
    this.perPage = size;
    // Reset to page 1 when page size changes
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Reload data with new page size and current filters
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed', this.currentFilters);
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft', this.currentFilters);
    } else {
      this.getContestLists(1, '', this.currentFilters);
    }
  }

  onPageIndexChange(index: number): void {
    if (this.tabs_grid === 'completed') {
      this.completedPageIndex = index;
      this.getContestLists(index, 'completed', this.currentFilters);
    } else if (this.tabs_grid === 'draft') {
      this.draftPageIndex = index;
      this.getContestLists(index, 'draft', this.currentFilters);
    } else {
      this.pageIndex = index;
      this.getContestLists(index, '', this.currentFilters);
    }
  }

  // getter for paged completed contests
  get pagedCompletedContests() {
    // Server-side pagination: return all items as backend already paginated
    return this.completedContests || [];
  }

  // Add method to get correct page range text based on active tab
  getActivePageRangeText(): string {
    if (this.tabs_grid === 'completed') {
      return this.getPageRangeText(this.completedContests, this.completedPageIndex);
    }
    return this.getPageRangeText(this.contest, this.pageIndex);
  }


  getStatusClass(status: string): string {
    switch (status) {
      case 'draft': return 'bg-secondary';
      case 'pending': return 'bg-warning';
      case 'approved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      case 'completed': return 'bg-success';
      case 'ongoing': return 'bg-info';
      case 'waiting_prize': return 'bg-purple';
      case 'waiting_publish': return 'bg-warning';
      case 'luqta_pending': return 'bg-luqta-pending';
      case 'web_approved': return 'bg-web-approved';
      default: return 'bg-status-default';
    }
  }

  // Calculate dynamic display status based on contest state
  getDisplayStatus(item: any): string {
    const now = new Date();
    const status = (item.status || '').toLowerCase();

    // If status is 'completed' (prize announced from backend), return as-is
    if (status === 'completed') {
      return 'completed';
    }

    // If status is luqta_pending, return as-is
    if (status === 'luqta_pending') {
      return 'luqta_pending';
    }

    // If status is not approved, return as-is (draft, pending, rejected)
    if (status !== 'approved') {
      return status;
    }

    // Contest is approved but not active yet - waiting to be published
    if (item.active !== true) {
      return 'waiting_publish';
    }

    // Contest is approved AND active - check dates to determine ongoing/waiting_prize
    const startDate = item.start_date ? new Date(item.start_date) : null;
    const endDate = item.end_date ? new Date(item.end_date) : null;

    // If contest has started but not ended yet -> Ongoing
    if (startDate && endDate && now >= startDate && now < endDate) {
      return 'ongoing';
    }

    // If contest has ended but prize not announced yet -> Waiting Prize
    // completed status comes from backend when prize is announced
    if (endDate && now >= endDate) {
      return 'waiting_prize';
    }

    // Contest approved and active but not started yet
    return 'approved';
  }

  // Access code is only meaningful for private contests; public contests
  // may have a placeholder value (e.g. 0) that shouldn't be displayed.
  getAccessCodeDisplay(item: any): string {
    const type = (item.type || item.contest_type || '').toLowerCase();
    return type === 'private' && item.access_code ? item.access_code : '-';
  }

  // Get display label for status
  getStatusLabel(status: string): string {
    const isAr = isArabicLang();
    switch (status) {
      case 'draft': return isAr ? 'مسودة' : 'Draft';
      case 'pending': return isAr ? 'قيد الانتظار' : 'Pending';
      case 'approved': return isAr ? 'معتمد' : 'Approved';
      case 'rejected': return isAr ? 'مرفوض' : 'Rejected';
      case 'completed': return isAr ? 'مكتمل' : 'Completed';
      case 'ongoing': return isAr ? 'نشط' : 'Ongoing';
      case 'waiting_prize': return isAr ? 'في انتظار الجائزة' : 'Waiting Prize';
      case 'waiting_publish': return isAr ? 'في انتظار النشر' : 'Waiting for Publish';
      case 'luqta_pending': return isAr ? 'قيد مراجعة لقطة' : 'Luqta Pending';
      case 'web_approved': return isAr ? 'معتمد للويب' : 'Web Approved';
      // Any unknown status coming from backend: turn "web_approved" into "Web Approved"
      default: return this.humanizeStatus(status);
    }
  }

  // Convert a raw snake_case/kebab-case status into a readable Title Case label
  private humanizeStatus(status: string): string {
    if (!status) return '-';
    return String(status)
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getCountryRecords() {
    this.sharedService.getCountryRecords().then((data) => {
      this.countryRecords = data;
    });
  }

  // / Add method to get correct total count
  getActiveTotalCount(): number {
    if (this.tabs_grid === 'completed') {
      return this.totalCompletedContestLength;
    } else if (this.tabs_grid === 'draft') {
      return this.totalDraftContestLength;
    }
    return this.totalContestLength;
  }

  onCurrentPageDataChange(data: readonly any[]): void {
    // Not needed for server-side pagination, but kept for template compatibility
  }

  onCompletedPageIndexChange(index: number): void {
    this.completedPageIndex = index;
    this.getContestLists(index, 'completed', this.currentFilters);
  }

  onDraftPageIndexChange(index: number): void {
    this.draftPageIndex = index;
    this.getContestLists(index, 'draft', this.currentFilters);
  }

  // Add method to get correct page index
  getActivePageIndex(): number {
    if (this.tabs_grid === 'completed') {
      return this.completedPageIndex;
    }
    return this.pageIndex;
  }

  logSomething(record: any) { }

  cancel(): void { }

  confirmDeleteContest(id: number, contestName?: string): void {
    const safeName = contestName || 'this engagement';
    this.modal.confirm({
      nzTitle: 'Delete Engagement',
      nzContent: `Are you sure you want to delete "${safeName}"?`,
      nzOkText: 'Delete',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzCentered: true,
      nzClassName: 'contest-styled-modal',
      nzOnOk: () => this.softDeleteContest(id),
    });
  }

  translatePrizeName(name: string): string {
    if (!name || name === 'N/A' || name === '-') return 'لا يوجد';
    let res = name.trim();

    const replacements: [RegExp, string][] = [
      // Full Specific Phones & Models
      [/\bSamsung Galaxy S24 Ultra\b/gi, 'سامسونج جالكسي S24 ألترا'],
      [/\bSamsung Galaxy S24\+\b/gi, 'سامسونج جالكسي S24 بلس'],
      [/\bSamsung Galaxy S24 Plus\b/gi, 'سامسونج جالكسي S24 بلس'],
      [/\bSamsung Galaxy S24\b/gi, 'سامسونج جالكسي S24'],
      [/\bSamsung Galaxy S23 Ultra\b/gi, 'سامسونج جالكسي S23 ألترا'],
      [/\bSamsung Galaxy S23\b/gi, 'سامسونج جالكسي S23'],
      [/\bSamsung Galaxy\b/gi, 'سامسونج جالكسي'],
      [/\bApple iPhone 17 Pro Max\b/gi, 'آبل آيفون 17 برو ماكس'],
      [/\bApple iPhone 17 Pro\b/gi, 'آبل آيفون 17 برو'],
      [/\bApple iPhone 17\b/gi, 'آبل آيفون 17'],
      [/\bApple iPhone 16 Pro Max\b/gi, 'آبل آيفون 16 برو ماكس'],
      [/\bApple iPhone 16 Pro\b/gi, 'آبل آيفون 16 برو'],
      [/\bApple iPhone 16 Plus\b/gi, 'آبل آيفون 16 بلس'],
      [/\bApple iPhone 16\b/gi, 'آبل آيفون 16'],
      [/\bApple iPhone 15 Pro Max\b/gi, 'آبل آيفون 15 برو ماكس'],
      [/\bApple iPhone 15 Pro\b/gi, 'آبل آيفون 15 برو'],
      [/\bApple iPhone 15 Plus\b/gi, 'آبل آيفون 15 بلس'],
      [/\bApple iPhone 15\b/gi, 'آبل آيفون 15'],
      [/\bApple iPhone 14 Pro Max\b/gi, 'آبل آيفون 14 برو ماكس'],
      [/\bApple iPhone 14 Pro\b/gi, 'آبل آيفون 14 برو'],
      [/\bApple iPhone 14\b/gi, 'آبل آيفون 14'],
      [/\bApple iPhone 13 Pro Max\b/gi, 'آبل آيفون 13 برو ماكس'],
      [/\bApple iPhone 13 Pro\b/gi, 'آبل آيفون 13 برو'],
      [/\bApple iPhone 13\b/gi, 'آبل آيفون 13'],
      [/\bApple iPhone\b/gi, 'آبل آيفون'],
      [/\biPhone 17 Pro Max\b/gi, 'آيفون 17 برو ماكس'],
      [/\biPhone 17 Pro\b/gi, 'آيفون 17 برو'],
      [/\biPhone 17\b/gi, 'آيفون 17'],
      [/\biPhone 16 Pro Max\b/gi, 'آيفون 16 برو ماكس'],
      [/\biPhone 16 Pro\b/gi, 'آيفون 16 برو'],
      [/\biPhone 16 Plus\b/gi, 'آيفون 16 بلس'],
      [/\biPhone 16\b/gi, 'آيفون 16'],
      [/\biPhone 15 Pro Max\b/gi, 'آيفون 15 برو ماكس'],
      [/\biPhone 15 Pro\b/gi, 'آيفون 15 برو'],
      [/\biPhone 15 Plus\b/gi, 'آيفون 15 بلس'],
      [/\biPhone 15\b/gi, 'آيفون 15'],
      [/\biPhone 14 Pro Max\b/gi, 'آيفون 14 برو ماكس'],
      [/\biPhone 14 Pro\b/gi, 'آيفون 14 برو'],
      [/\biPhone 14\b/gi, 'آيفون 14'],
      [/\biPhone 13 Pro Max\b/gi, 'آيفون 13 برو ماكس'],
      [/\biPhone 13 Pro\b/gi, 'آيفون 13 برو'],
      [/\biPhone 13\b/gi, 'آيفون 13'],
      [/\biPhone\b/gi, 'آيفون'],
      [/\bApple iPad Air 5th Gen\b/gi, 'آبل آيباد اير الجيل الخامس'],
      [/\bApple iPad Air\b/gi, 'آبل آيباد اير'],
      [/\bApple iPad Pro\b/gi, 'آبل آيباد برو'],
      [/\bApple iPad\b/gi, 'آبل آيباد'],
      [/\biPad Air\b/gi, 'آيباد اير'],
      [/\biPad Pro\b/gi, 'آيباد برو'],
      [/\biPad\b/gi, 'آيباد'],
      [/\bApple Macbook Air M2\b/gi, 'آبل ماك بوك اير M2'],
      [/\bApple Macbook Air\b/gi, 'آبل ماك بوك اير'],
      [/\bApple Macbook Pro\b/gi, 'آبل ماك بوك برو'],
      [/\bApple Macbook\b/gi, 'آبل ماك بوك'],
      [/\bMacbook Air\b/gi, 'ماك بوك اير'],
      [/\bMacbook Pro\b/gi, 'ماك بوك برو'],
      [/\bMacBook\b/gi, 'ماك بوك'],
      [/\bApple Watch Series 9\b/gi, 'ساعة آبل (Series 9)'],
      [/\bApple Watch Ultra\b/gi, 'ساعة آبل الترا'],
      [/\bApple Watch\b/gi, 'ساعة آبل'],
      [/\bpurple phone\b/gi, 'هاتف باللون البنفسجي'],
      [/\bmobile phone\b/gi, 'هاتف جوال'],
      [/\bmobile\b/gi, 'هاتف جوال'],
      [/\bphone\b/gi, 'هاتف'],
      [/\bcharger\b/gi, 'شاحن'],
      [/\bSmart Watch\b/gi, 'ساعة ذكية'],
      [/\bSmartwatch\b/gi, 'ساعة ذكية'],
      [/\bFitness Tracker\b/gi, 'سوار تتبع اللياقة البدنية'],

      // Capacities
      [/\b512\s*GB\b/gi, '512 جيجابايت'],
      [/\b256\s*GB\b/gi, '256 جيجابايت'],
      [/\b128\s*GB\b/gi, '128 جيجابايت'],
      [/\b64\s*GB\b/gi, '64 جيجابايت'],
      [/\b1\s*TB\b/gi, '1 تيرابايت'],

      // Electronics / Gaming / Experiences
      [/\bPlayStation 5 Console\b/gi, 'جهاز بلايستيشن 5'],
      [/\bPS5 Console Bundle\b/gi, 'حزمة جهاز بلايستيشن 5'],
      [/\bPS5 Console\b/gi, 'جهاز بلايستيشن 5'],
      [/\bPlayStation 5\b/gi, 'بلايستيشن 5'],
      [/\bPS5\b/gi, 'بلايستيشن 5'],
      [/\bNikon D850 Camera\b/gi, 'كاميرا نيكون D850'],
      [/\bDJI Mini 3 Pro Drone\b/gi, 'طائرة درون DJI Mini 3 Pro'],
      [/\bDJI Air 2S Drone\b/gi, 'طائرة درون DJI Air 2S'],
      [/\bWacom Cintiq 16\b/gi, 'لوح رسم واكوم سينتيك 16'],
      [/\bAR Hunt Kit\b/gi, 'طقم صيد الواقع المعزز (AR)'],
      [/\bTravel Package to Dubai\b/gi, 'باقة سفر إلى دبي'],
      [/\bCooking Class Experience with Chef\b/gi, 'تجربة دورة طبخ مع شيف'],
      [/\bCooking Class Experience\b/gi, 'تجربة دورة طبخ'],
      [/\bCooking Class\b/gi, 'دورة طبخ'],
      [/\bNescafe Coffee Machine\b/gi, 'ماكينة قهوة نسكافيه'],
      [/\bCoffee Machine\b/gi, 'ماكينة قهوة'],
      [/\bKitchenAid Stand Mixer\b/gi, 'عجانة كيتشن إيد'],
      [/\bStand Mixer\b/gi, 'عجانة كهربائية'],
      [/\bMixer\b/gi, 'خلاط'],
      [/\bHockey Stick\b/gi, 'عصا الهوكي'],
      [/\bNHL Game Tickets\b/gi, 'تذاكر مباريات NHL'],
      [/\bFlexibility Champion Award\b/gi, 'جائزة بطل المرونة'],
      [/\bChampion Award\b/gi, 'جائزة البطل'],
      [/\bAward\b/gi, 'جائزة'],
      [/\bcompetition\b/gi, 'مسابقة'],
      [/\bGaming Gift card\b/gi, 'بطاقة هدايا ألعاب'],
      [/\bGaming Gift Card\b/gi, 'بطاقة هدايا ألعاب'],
      [/\bAmazon Gift Card\b/gi, 'بطاقة هدايا أمازون'],
      [/\bNoon Gift Voucher\b/gi, 'قسيمة هدايا نون'],
      [/\bNeon Gift Voucher\b/gi, 'قسيمة هدايا نيون'],
      [/\bNoon Food\b/gi, 'نون فود'],
      [/\bCoffee Voucher\b/gi, 'قسيمة قهوة'],
      [/\bGift Vouchers?\b/gi, 'قسائم هدايا'],
      [/\bGift Card\b/gi, 'بطاقة هدايا'],
      [/\bEgyptian Pounds\b/gi, 'جنيه مصري'],
      [/\bEgyptian Pound\b/gi, 'جنيه مصري'],
      [/\bprize\b/gi, 'جائزة'],
      [/\bCamera\b/gi, 'كاميرا'],
      [/\bDrone\b/gi, 'طائرة درون'],
      [/\bTickets\b/gi, 'تذاكر'],
      [/\bConsole\b/gi, 'جهاز ألعاب'],
      [/\bVoucher\b/gi, 'قسيمة'],

      // Test Data & Author Names in prizes
      [/\bOlga Brock\b/gi, 'أولغا بروك'],
      [/\bDesirae Berry\b/gi, 'ديزيري بيري'],
      [/\bSasha Gutierrez\b/gi, 'ساشا غوتيريز'],
      [/\bPrescott Bowen\b/gi, 'بريسكوت بوين'],
      [/\bKristen Buckner\b/gi, 'كريستن باكنر'],
      [/\bTest contest start date\b/gi, 'تاريخ بدء مسابقة الاختبار'],
      [/\bawedfsaf\b/gi, 'جائزة تجريبية'],
      [/\bdasd\b/gi, 'جائزة تجريبية'],
      [/\bdas\b/gi, 'جائزة تجريبية'],
      [/\bdhdf\b/gi, 'جائزة تجريبية'],
      [/\bpomv\b/gi, 'جائزة تجريبية'],
      [/\bxxx\b/gi, 'جائزة تجريبية'],
      [/\baaa\b/gi, 'جائزة تجريبية'],
      [/\btest\b/gi, 'جائزة تجريبية'],
      [/\bNostrum et aliquid[^\s,]*/gi, 'جائزة خاصة'],
      [/\bAccusanda voluptate[^\s,]*/gi, 'جائزة خاصة'],
      [/\bAsperiores sapiente[^\s,]*/gi, 'جائزة خاصة'],
      [/\bEos officiis[^\s,]*/gi, 'جائزة خاصة'],

      // Currencies
      [/\bSAR\b/g, 'ريال سعودي'],
      [/\bAED\b/g, 'درهم إماراتي'],
      [/\bUSD\b/g, 'دولار أمريكي'],
      [/\bEGP\b/g, 'جنيه مصري']
    ];

    for (const [pattern, replacement] of replacements) {
      res = res.replace(pattern, replacement);
    }

    return res;
  }

  translateContestName(name: string): string {
    if (!name || name === 'N/A' || name === '-') return 'غير متوفر';
    let res = name.trim();

    if (/[\u0600-\u06FF]/.test(res) && !/[a-zA-Z]/.test(res)) {
      return res;
    }

    const contestNameReplacements: [RegExp, string][] = [
      [/\bspider\s*man\s*brand\s*new\s*day\b/gi, 'سبايدرمان: يوم جديد'],
      [/\bspider\s*man\b/gi, 'سبايدرمان'],
      [/\bbrand\s*new\s*day\b/gi, 'يوم جديد'],
      [/\bhaze\s*challenge\b/gi, 'تحدي الضباب'],
      [/\bTest\s*contest\s*start\s*date\b/gi, 'اختبار تاريخ بدء المسابقة'],
      [/\btest\s*by\s*notification\b/gi, 'اختبار عبر الإشعار'],
      [/\btest\s*by\s*public\b/gi, 'اختبار عام'],
      [/\btest\s*by\s*backend\b/gi, 'اختبار عبر النظام'],
      [/\btest\s*private\s*code\b/gi, 'اختبار الرمز الخاص'],
      [/\btest\s*sdk\s*pr\b/gi, 'اختبار SDK'],
      [/\btest\s*rating\b/gi, 'اختبار التقييم'],
      [/\btest\s*survey\b/gi, 'اختبار استبيان'],
      [/\btest\s*pointas?\b/gi, 'اختبار النقاط'],
      [/\btest\s*ar\b/gi, 'اختبار عربي'],
      [/\bcolling\s*contest\b/gi, 'مسابقة الاتصال'],
      [/\bcalling\s*contest\b/gi, 'مسابقة الاتصال'],
      [/\bcontest\s*(\d+)\b/gi, 'مسابقة $1'],
      [/\bcontest\b/gi, 'مسابقة'],
      [/\bchallenge\b/gi, 'تحدي'],
      [/\bNotification\b/gi, 'إشعار'],
      [/\bnotif\b/gi, 'إشعار'],
      [/\bSurvey\b/gi, 'استبيان'],
      [/\bRating\b/gi, 'تقييم'],
      [/\bStart Date\b/gi, 'تاريخ البدء'],
      [/\bIran\b/gi, 'إيران'],
      [/\bmin\s*gamea\b/gi, 'من جميعاً'],
      [/\bhi\d*\b/gi, 'مرحباً'],
      [/\bhello\b/gi, 'مرحباً'],
      [/\bhollow\b/gi, 'مرحباً'],
      [/\bTest\b/gi, 'اختبار'],
      [/\btest\b/gi, 'اختبار'],
      [/\basdas?\b/gi, 'اختبار تجريبي'],
      [/\bdasd\b/gi, 'اختبار تجريبي'],
      [/\bdas\b/gi, 'اختبار تجريبي'],
      [/\bdssdas\b/gi, 'اختبار تجريبي'],
      [/\bct0\d*\b/gi, 'مسابقة تجريبية'],
      [/\bct-1\b/gi, 'مسابقة تجريبية 1'],
      [/\bOlga Brock\b/gi, 'أولغا بروك'],
      [/\bDesirae Berry\b/gi, 'ديزيري بيري'],
      [/\bSasha Gutierrez\b/gi, 'ساشا غوتيريز'],
      [/\bPrescott Bowen\b/gi, 'بريسكوت بوين'],
      [/\bKristen Buckner\b/gi, 'كريستن باكنر'],
      [/\bAccusanda voluptate[^\s,]*/gi, 'مسابقة تجريبية'],
      [/\bAsperiores sapiente[^\s,]*/gi, 'مسابقة تجريبية'],
      [/\bEos officiis[^\s,]*/gi, 'مسابقة تجريبية'],
      [/\bNostrum et aliquid[^\s,]*/gi, 'مسابقة تجريبية'],
    ];

    for (const [pattern, replacement] of contestNameReplacements) {
      res = res.replace(pattern, replacement);
    }

    return res;
  }

  getPrizeNames(prizes: any[]): string {
    const isAr = isArabicLang();
    if (prizes && prizes.length > 0) {
      return prizes
        .map(p => {
          if (isAr) {
            const arName = (p.arabic_name || p.arabicName || p.name_ar || p.prize_name_ar)?.trim();
            if (arName) return arName;
            const engName = p.name || p.prize_name || p.title || '';
            return this.translatePrizeName(engName);
          }
          return p.name || p.prize_name || p.title || '';
        })
        .filter(Boolean)
        .join(isAr ? '، ' : ', ') || '-';
    }
    return '-';
  }

  // Location display helpers
  getLocationArray(item: any): string[] {
    const locationString = item.location || item.contest_location || '-';
    if (locationString === '-') return ['-'];
    return locationString.split(',').map((loc: string) => loc.trim());
  }

  getFirstTwoLocations(item: any): string {
    const locations = this.getLocationArray(item);
    return locations.slice(0, 2).join(', ');
  }

  getRemainingLocations(item: any): string {
    const locations = this.getLocationArray(item);
    return locations.slice(2).join(', ');
  }

  getRemainingLocationCount(item: any): number {
    const locations = this.getLocationArray(item);
    return locations.length - 2;
  }

  hasMoreThanTwoLocations(item: any): boolean {
    const locations = this.getLocationArray(item);
    return locations.length > 2;
  }

  // animate count 


  // Active tab handler
  setActiveTab(tab: 'all_contest' | 'completed' | 'draft') {
    this.tabs_grid = tab;

    // Reset page index when switching tabs
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API with appropriate status filter and current filters
    if (tab === 'all_contest') {
      this.getContestLists(1, '', this.currentFilters);
    }
    else if (tab === 'completed') {
      this.getContestLists(1, 'completed', this.currentFilters);
    }
    else if (tab === 'draft') {
      this.getContestLists(1, 'draft', this.currentFilters);
    }
  }


  // Methods add karein
  toggleShareMenu(event: Event, index: number): void {
    event.stopPropagation();

    if (this.activeShareMenuIndex === index) {
      this.activeShareMenuIndex = null;
    } else {
      this.activeShareMenuIndex = index;
    }
  }

  closeShareMenu(): void {
    this.activeShareMenuIndex = null;
  }

  showShareIcons(id: number): void {
    this.activeShareId = id;
  }

  hideShareIcons(): void {
    this.activeShareId = null;
  }

  private buildPublicContestShareUrl(contestId: number | string): string {
    const canonicalBaseUrl = 'https://luqta.io';
    return `${canonicalBaseUrl}/detailContestPage?navigationPage=homePage&contestId=${contestId}`;
  }

  shareOnSocial(event: Event, platform: string, contestData: any): void {
    event.stopPropagation();

    // Generate Luqta deep link URL
    const contestUrl = this.buildPublicContestShareUrl(contestData.id);
    const contestName = contestData.name || 'Engagement';

    // Share message template
    const shareMessage = `🎉 The ${contestName} engagement is now live on Luqta!\nJoin now and win exciting prizes — don't miss your chance!\n👉 ${contestUrl}`;

    switch (platform) {
      case 'twitter':
        const twitterText = `🎉 ${contestName} engagement is now live on Luqta!\nJoin now and win exciting prizes! 🎁`;
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(contestUrl)}`,
          '_blank'
        );
        break;

      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
          '_blank'
        );
        break;

      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(contestUrl)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        const linkedinTitle = `Join the ${contestName} engagement on Luqta!`;
        const linkedinSummary = `🎉 The ${contestName} engagement is now live on Luqta! Join now and win exciting prizes — don't miss your chance!`;
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(contestUrl)}`,
          '_blank'
        );
        break;

      case 'copy':
        // Copy link to clipboard
        navigator.clipboard.writeText(contestUrl).then(() => {
          this.sharedService.createNotification('success', 'Engagement link copied to clipboard!');
        }).catch(() => {
          this.sharedService.createNotification('error', 'Failed to copy link');
        });
        // Don't close menu on copy - let user share to social media after copying
        return;
    }

    // Close menu after sharing to social platforms
    this.closeShareMenu();
  }

  // Helper method to check if contest is shareable
  isContestShareable(contest: any): boolean {
    const status = (contest.status || contest.approval_status || '').toLowerCase();
    return status === 'approved' && !contest.isExpired;
  }

  getPercentageChange(current: number, previous: number): { value: number, isPositive: boolean } {
    if (previous === 0) {
      return { value: 0, isPositive: true };
    }
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0
    };
  }

  getTrendBadge(trend: number | null | undefined): { value: number, isPositive: boolean } {
    const safe = typeof trend === 'number' && isFinite(trend) ? trend : 0;
    return { value: Math.abs(safe), isPositive: safe >= 0 };
  }

  downloadContest() {
    const exportConfig = this.getExportConfigByTab();
    this.fetchAllContestsForExport(exportConfig.status, (contestsToExport) => {
      this.generateContestPDF(contestsToExport, exportConfig.reportTitle, exportConfig.fileName).catch(() => {
        this.sharedService.createNotification('error', 'Failed to generate PDF');
      });
    });
  }

  // Per-contest "Download Configuration as PDF". Fetches the full contest record
  // (creation-form fields + submission metadata are not present on the list rows)
  // and hands it to the config PDF builder.
  async downloadContestConfig(item: any): Promise<void> {
    if (!item?.id) {
      return;
    }
    this.sharedService.showLoader();
    try {
      const contest: any = await firstValueFrom(this.pagesService.getById('GET_SINGLE_CONTEST', item.id));
      if (!contest) {
        this.sharedService.createNotification('error', 'Contest details not found');
        return;
      }
      await this.generateContestConfigPDF(contest);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to generate contest configuration PDF');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  private pdfConfigSectionHeading(doc: jsPDF, text: string, y: number): number {
    const pageWidth = doc.internal.pageSize.width;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 52, 137);
    doc.text(text, 14, y);
    doc.setDrawColor(60, 52, 137);
    doc.setLineWidth(0.4);
    doc.line(14, y + 1.5, pageWidth - 14, y + 1.5);
    return y + 8;
  }

  private pdfConfigEnsureSpace(doc: jsPDF, y: number, needed: number): number {
    const pageHeight = doc.internal.pageSize.height;
    if (y + needed > pageHeight - 15) {
      doc.addPage();
      return 15;
    }
    return y;
  }

  private getPdfStatusColors(status: string): { bg: number[]; text: number[] } {
    const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
    switch (normalized) {
      case 'approved': return { bg: [212, 237, 218], text: [21, 87, 36] };
      case 'pending': return { bg: [255, 236, 179], text: [134, 100, 0] };
      case 'completed': return { bg: [217, 245, 232], text: [16, 122, 78] };
      case 'draft': return { bg: [226, 227, 229], text: [56, 61, 65] };
      case 'ongoing': return { bg: [210, 229, 255], text: [22, 95, 168] };
      case 'waiting_prize': return { bg: [235, 220, 250], text: [104, 57, 145] };
      case 'waiting_publish': return { bg: [255, 236, 179], text: [134, 100, 0] };
      case 'rejected': return { bg: [250, 219, 219], text: [153, 27, 27] };
      case 'luqta_pending': return { bg: [255, 231, 217], text: [154, 74, 22] };
      default: return { bg: [245, 245, 245], text: [85, 85, 85] };
    }
  }

  // Renders Arabic text as a wrapped, right-aligned PNG via canvas. The browser
  // handles bidi shaping, contextual forms and word-wrapping correctly — which
  // jsPDF's single-line processArabic cannot (it clips long text and reverses
  // multi-line order). Returns the data URL plus its pixel dimensions.
  private renderArabicImage(
    text: string,
    maxWidthMm: number,
    fontSizePt: number
  ): { dataUrl: string; widthMm: number; heightMm: number; widthPx: number; heightPx: number } | null {
    try {
      const pxPerMm = 10;
      const maxWidthPx = Math.max(60, Math.round(maxWidthMm * pxPerMm));
      const fontPx = Math.max(13, Math.round(fontSizePt * 3.6));
      const lineH = Math.round(fontPx * 1.55);
      const padPx = 6;
      const fontSpec = `500 ${fontPx}px "Segoe UI", "Tahoma", "Arial", sans-serif`;

      // Pass 1: measure and wrap into lines.
      const measureCanvas = document.createElement('canvas');
      const mctx = measureCanvas.getContext('2d');
      if (!mctx) return null;
      mctx.font = fontSpec;
      const words = String(text).replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
      const lines: string[] = [];
      let current = '';
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (mctx.measureText(candidate).width > maxWidthPx - padPx * 2 && current) {
          lines.push(current);
          current = word;
        } else {
          current = candidate;
        }
      }
      if (current) lines.push(current);
      if (!lines.length) return null;

      // Pass 2: draw at 2x for crispness.
      const wPx = maxWidthPx;
      const hPx = lines.length * lineH + padPx * 2;
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = wPx * scale;
      canvas.height = hPx * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.scale(scale, scale);
      ctx.font = fontSpec;
      ctx.fillStyle = '#333333';
      ctx.direction = 'rtl';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      lines.forEach((line, i) => {
        ctx.fillText(line, wPx - padPx, padPx + lineH * (i + 0.5));
      });

      return {
        dataUrl: canvas.toDataURL('image/png'),
        widthMm: wPx / pxPerMm,
        heightMm: hPx / pxPerMm,
        widthPx: wPx,
        heightPx: hPx,
      };
    } catch {
      return null;
    }
  }

  // Shared autoTable hooks so Arabic values in config tables render correctly.
  // Arabic cells are drawn as wrapped RTL images (blanking the native text) so
  // long values (e.g. the Arabic description) wrap instead of clipping.
  // `wrapWidthMm` is the approximate text width of the column holding Arabic.
  private arabicAwareTableHooks(
    doc: jsPDF,
    arabicFontLoaded: boolean,
    wrapWidthMm: number = 115
  ): { didParseCell: (data: any) => void; didDrawCell: (data: any) => void } {
    return {
      didParseCell: (data: any) => {
        if (data.section !== 'body') return;
        const raw = String(data.cell.raw ?? '');
        if (!this.containsArabic(raw)) return;
        data.cell.styles.halign = 'right';

        const fontSize = data.cell.styles?.fontSize || 9;
        const img = this.renderArabicImage(raw, wrapWidthMm, fontSize);
        if (img) {
          (data.cell as any)._arabicImg = img;
          data.cell.text = [''];
          const needed = img.heightMm + 3;
          const currentMin = data.cell.styles.minCellHeight || 0;
          if (currentMin < needed) data.cell.styles.minCellHeight = needed;
          return;
        }

        // Fallbacks if canvas rendering is unavailable.
        if (arabicFontLoaded) {
          data.cell.styles.font = 'Amiri';
          const processArabic = (doc as any).processArabic;
          data.cell.text = [typeof processArabic === 'function' ? processArabic(raw) : raw];
        } else {
          data.cell.text = [''];
        }
      },
      didDrawCell: (data: any) => {
        const img = (data.cell as any)?._arabicImg;
        if (img) {
          const pad = 2;
          const availW = data.cell.width - pad * 2;
          const drawW = Math.min(img.widthMm, availW);
          const drawH = drawW * (img.heightPx / img.widthPx);
          const x = data.cell.x + data.cell.width - drawW - pad; // right-aligned
          const y = data.cell.y + Math.max(pad, (data.cell.height - drawH) / 2);
          try { doc.addImage(img.dataUrl, 'PNG', x, y, drawW, drawH); } catch { /* ignore */ }
          return;
        }
        if (!arabicFontLoaded && data.section === 'body') {
          const raw = String(data.cell.raw ?? '');
          if (this.containsArabic(raw)) {
            this.drawArabicCellAsImage(doc, raw, data.cell.x, data.cell.y, data.cell.width, data.cell.height);
          }
        }
      }
    };
  }

  // Composes the mobile-preview card background on a canvas: a cover-fit banner,
  // rounded corners, and a genuinely smooth bottom gradient. Returns a PNG data URL
  // (transparent corners) so the PDF shows no border frame and no gradient banding.
  private composeMobileCardImage(bannerDataUrl: string | null, widthMm: number, heightMm: number): Promise<string | null> {
    return new Promise((resolve) => {
      try {
        const scale = 8; // px per mm
        const w = Math.round(widthMm * scale);
        const h = Math.round(heightMm * scale);
        const radius = 6 * scale;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }

        const roundedPath = () => {
          ctx.beginPath();
          ctx.moveTo(radius, 0);
          ctx.arcTo(w, 0, w, h, radius);
          ctx.arcTo(w, h, 0, h, radius);
          ctx.arcTo(0, h, 0, 0, radius);
          ctx.arcTo(0, 0, w, 0, radius);
          ctx.closePath();
        };

        roundedPath();
        ctx.save();
        ctx.clip();
        ctx.fillStyle = '#e8e8f1';
        ctx.fillRect(0, 0, w, h);

        const finish = () => {
          // Smooth dark gradient over the bottom half for text legibility.
          const gradH = h * 0.52;
          const grad = ctx.createLinearGradient(0, h - gradH, 0, h);
          grad.addColorStop(0, 'rgba(6,6,14,0)');
          grad.addColorStop(0.55, 'rgba(6,6,14,0.55)');
          grad.addColorStop(1, 'rgba(6,6,14,0.92)');
          ctx.fillStyle = grad;
          ctx.fillRect(0, h - gradH, w, gradH);
          ctx.restore();
          resolve(canvas.toDataURL('image/png'));
        };

        if (bannerDataUrl) {
          const img = new Image();
          img.onload = () => {
            // object-fit: cover
            const imgRatio = img.width / img.height;
            const boxRatio = w / h;
            let dw: number, dh: number, dx: number, dy: number;
            if (imgRatio > boxRatio) {
              dh = h; dw = h * imgRatio; dx = (w - dw) / 2; dy = 0;
            } else {
              dw = w; dh = w / imgRatio; dx = 0; dy = (h - dh) / 2;
            }
            ctx.drawImage(img, dx, dy, dw, dh);
            finish();
          };
          img.onerror = () => finish();
          img.src = bannerDataUrl;
        } else {
          finish();
        }
      } catch {
        resolve(null);
      }
    });
  }

  // Loads the prize-category taxonomy (CATEGORIES_LOOKUP) once and builds an
  // id -> name map so prize category IDs can be shown as readable names.
  private categoryNameMapCache: Map<number, string> | null = null;
  private async getCategoryNameMap(): Promise<Map<number, string>> {
    if (this.categoryNameMapCache) return this.categoryNameMapCache;
    const map = new Map<number, string>();
    try {
      const res: any = await firstValueFrom(this.pagesService.getAll('CATEGORIES_LOOKUP'));
      const list = Array.isArray(res)
        ? res
        : (Array.isArray(res?.items) ? res.items : (Array.isArray(res?.data) ? res.data : []));
      const walk = (nodes: any[]) => {
        for (const n of nodes) {
          if (n && n.id != null) {
            map.set(Number(n.id), String(n.name ?? n.title ?? n.label ?? n.id));
          }
          const kids = n?.children ?? n?.subcategories ?? n?.sub_categories;
          if (Array.isArray(kids)) walk(kids);
        }
      };
      walk(list);
    } catch {
      // Lookup unavailable — resolveCategoryNames will fall back to raw ids.
    }
    this.categoryNameMapCache = map;
    return map;
  }

  // Turns a category value (id, id array, or object array) into a comma-separated
  // list of names using the lookup map; falls back to the raw id if unmapped.
  private resolveCategoryNames(raw: any, nameMap: Map<number, string>): string {
    const items = Array.isArray(raw)
      ? raw
      : (raw === null || raw === undefined || raw === '' ? [] : [raw]);
    const names = items.map((item: any) => {
      let id: any = item;
      let name = '';
      if (item && typeof item === 'object') {
        id = item.id ?? item.category_id ?? item.categoryId;
        name = item.name ?? item.title ?? item.label ?? '';
      }
      if (name) return name;
      const num = Number(id);
      if (!isNaN(num) && nameMap.has(num)) return nameMap.get(num) as string;
      return id != null ? String(id) : '';
    }).filter(Boolean);
    return names.join(', ');
  }

  // Builds a portrait "Contest Configuration" PDF that mirrors the creation form:
  // a mobile-app preview card, submission metadata, and every configured field
  // (basic info, setup, prizes, levels).
  private async generateContestConfigPDF(contest: any): Promise<void> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;

    // --- Preload raster assets (all CORS-safe via SharedService) ---
    const logoData = await this.sharedService.getLuqtaLogoDataUrl();
    const bannerUrl = contest?.banners?.[0]?.url || contest?.banners?.[0]?.path || '';
    const bannerData = bannerUrl ? await this.sharedService.getImageDataUrl(bannerUrl) : null;
    const storedClientLogo = localStorage.getItem('profileImage');
    const clientLogoSrc = storedClientLogo && storedClientLogo !== 'assets/images/profile.png'
      ? storedClientLogo
      : 'assets/images/luqta_favicon2.png';
    const clientLogoData = await this.sharedService.getImageDataUrl(clientLogoSrc);
    const arabicFontLoaded = await this.ensureArabicFont(doc);
    const categoryNameMap = await this.getCategoryNameMap();

    // --- Formatting helpers ---
    const stripHtml = (v: any) => String(v ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const fmtDate = (d: any) => {
      if (!d) return 'N/A';
      const date = new Date(d);
      return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString('en-GB');
    };
    const fmtArr = (v: any) => {
      const items = Array.isArray(v) ? v : (v === null || v === undefined || v === '' ? [] : [v]);
      const parts = items.map((item: any) => {
        if (item === null || item === undefined) return '';
        if (typeof item === 'object') {
          if (item.name) return String(item.name);
          if (item.label) return String(item.label);
          if (item.title) return String(item.title);
          if (item.min != null && item.max != null) return `${item.min}-${item.max}`;
          return '';
        }
        return String(item).trim();
      }).filter(Boolean);
      return parts.length ? parts.join(', ') : 'N/A';
    };
    const val = (v: any) => {
      const s = String(v ?? '').trim();
      return s || 'N/A';
    };
    // Returns the first key whose value is present (handles the API's snake_case /
    // camelCase / legacy variants), so fields don't fall back to N/A on a key miss.
    const pick = (...keys: string[]): any => {
      for (const k of keys) {
        const v = contest?.[k];
        if (v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '') && !(Array.isArray(v) && v.length === 0)) {
          return v;
        }
      }
      return undefined;
    };
    const pickLevel = (lvl: any, ...keys: string[]): any => {
      for (const k of keys) {
        const v = lvl?.[k];
        if (v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '') && !(Array.isArray(v) && v.length === 0)) {
          return v;
        }
      }
      return undefined;
    };

    // ================= Header bar =================
    doc.setFillColor(60, 52, 137);
    doc.rect(0, 0, pageWidth, 24, 'F');
    if (logoData) {
      try { doc.addImage(logoData, 'PNG', margin, 7, 10, 10); } catch { /* ignore */ }
    }
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Contest Configuration', pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(val(contest?.name), pageWidth / 2, 18, { align: 'center' });

    let y = 30;

    const levelsArr = Array.isArray(contest?._levels_of_contest) ? contest._levels_of_contest : [];
    const displayStatus = this.getDisplayStatus(contest);
    const statusLabel = this.getStatusLabel(displayStatus);

    // ================= Top section: mobile preview + submission meta =================
    // Mirrors the review-card in the contest creation form: a full-bleed banner
    // with the client logo, time/levels badges, title and description overlaid.
    const cardX = margin;
    const cardW = 66;
    const cardTop = y;
    const cardH = 108;
    const contentX = cardX + 5;

    // --- Card background: cover-fit banner + rounded corners + smooth bottom
    // gradient, composed on a canvas (no border frame, no gradient banding). ---
    const cardImg = await this.composeMobileCardImage(bannerData, cardW, cardH);
    if (cardImg) {
      try { doc.addImage(cardImg, 'PNG', cardX, cardTop, cardW, cardH); }
      catch { doc.setFillColor(236, 236, 241); doc.roundedRect(cardX, cardTop, cardW, cardH, 6, 6, 'F'); }
    } else {
      doc.setFillColor(236, 236, 241);
      doc.roundedRect(cardX, cardTop, cardW, cardH, 6, 6, 'F');
    }

    // --- Client logo — top-left over banner (no extra frame; the logo image
    // already carries its own background, so a backing frame would double-border) ---
    const avSize = 15;
    const avX = cardX + 5;
    const avY = cardTop + 5;
    if (clientLogoData) {
      try { doc.addImage(clientLogoData, 'JPEG', avX, avY, avSize, avSize); }
      catch { doc.setFillColor(238, 238, 243); doc.roundedRect(avX, avY, avSize, avSize, 2.5, 2.5, 'F'); }
    } else {
      doc.setFillColor(238, 238, 243);
      doc.roundedRect(avX, avY, avSize, avSize, 2.5, 2.5, 'F');
    }

    // --- Layout the bottom content, anchored to the card bottom ---
    const detailLines = doc.splitTextToSize(stripHtml(contest?.details) || 'No description provided.', cardW - 10);
    const descCount = Math.min(detailLines.length, 2);
    const descLastY = cardTop + cardH - 8;
    const descFirstY = descLastY - (descCount - 1) * 3.9;
    const titleY = descFirstY - 7;
    const badgeY = titleY - 9;

    // --- Badges: time-left + levels count (over gradient) ---
    const timeLeft = contest?.end_date ? this.sharedService.timeRemaining(contest.end_date) : '';
    let badgeX = contentX;
    const drawBadge = (text: string, bg: number[], fg: number[]) => {
      doc.setFontSize(6.8);
      doc.setFont('helvetica', 'bold');
      const tw = doc.getTextWidth(text) + 5;
      doc.setFillColor(bg[0], bg[1], bg[2]);
      doc.roundedRect(badgeX, badgeY, tw, 5.5, 2.5, 2.5, 'F');
      doc.setTextColor(fg[0], fg[1], fg[2]);
      doc.text(text, badgeX + 2.5, badgeY + 3.7);
      badgeX += tw + 2.5;
    };
    if (timeLeft) drawBadge(timeLeft, [255, 255, 255], [80, 62, 170]);
    drawBadge(`Levels (${levelsArr.length})`, [255, 255, 255], [16, 118, 78]);

    // --- Title (white, over gradient) ---
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    const nameLines = doc.splitTextToSize(val(contest?.name), cardW - 10);
    doc.text(nameLines.slice(0, 1), contentX, titleY);

    // --- Description (white, up to 2 lines) ---
    doc.setTextColor(238, 238, 243);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text(detailLines.slice(0, 2), contentX, descFirstY);

    // ---- Submission details box (right of preview) ----
    const infoX = cardX + cardW + 8;
    const infoW = pageWidth - margin - infoX;
    const infoTop = cardTop;
    const infoH = cardH;
    doc.setFillColor(250, 250, 252);
    doc.setDrawColor(224, 224, 230);
    doc.setLineWidth(0.4);
    doc.roundedRect(infoX, infoTop, infoW, infoH, 5, 5, 'FD');
    // Title strip
    doc.setFillColor(60, 52, 137);
    doc.roundedRect(infoX, infoTop, infoW, 11, 5, 5, 'F');
    doc.rect(infoX, infoTop + 6, infoW, 5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Submission Details', infoX + 5, infoTop + 7.2);

    const submittedBy = contest?.submitted_by?.name || 'N/A';
    const submittedByEmail = contest?.submitted_by?.email || '';
    const submittedVia = contest?.submitted_via || 'N/A';
    const submittedOn = fmtDate(contest?.submitted_on ?? contest?.created_at);
    const approvedBy = contest?.approved_by?.name || 'Not yet approved';
    const approvedByEmail = contest?.approved_by?.email || '';

    // Rows are evenly distributed across the box height so there is no dead space.
    const infoLabelX = infoX + 5;
    const rowsTop = infoTop + 11;
    const rowsBottom = infoTop + infoH;
    const rowStep = (rowsBottom - rowsTop) / 5;
    const drawInfoRow = (index: number, label: string, value: string, opts?: { sub?: string; badge?: boolean }) => {
      const rowCenter = rowsTop + rowStep * index + rowStep / 2;
      if (index > 0) {
        doc.setDrawColor(233, 233, 238);
        doc.setLineWidth(0.3);
        doc.line(infoLabelX, rowsTop + rowStep * index, infoX + infoW - 5, rowsTop + rowStep * index);
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(130, 130, 140);
      doc.text(label.toUpperCase(), infoLabelX, rowCenter - 3);
      if (opts?.badge) {
        const sc = this.getPdfStatusColors(displayStatus);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        const bw2 = doc.getTextWidth(value) + 7;
        doc.setFillColor(sc.bg[0], sc.bg[1], sc.bg[2]);
        doc.roundedRect(infoLabelX, rowCenter, bw2, 6.5, 2, 2, 'F');
        doc.setTextColor(sc.text[0], sc.text[1], sc.text[2]);
        doc.text(value, infoLabelX + 3.5, rowCenter + 4.5);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(38, 38, 44);
        const lines = doc.splitTextToSize(value, infoW - 10);
        doc.text(lines.slice(0, 1), infoLabelX, rowCenter + 3);
        if (opts?.sub) {
          doc.setFontSize(7.5);
          doc.setTextColor(148, 148, 156);
          const subLines = doc.splitTextToSize(opts.sub, infoW - 10);
          doc.text(subLines.slice(0, 1), infoLabelX, rowCenter + 7.5);
        }
      }
    };
    drawInfoRow(0, 'Submitted by', submittedBy, { sub: submittedByEmail });
    drawInfoRow(1, 'Submitted via', submittedVia);
    drawInfoRow(2, 'Submitted on', submittedOn);
    drawInfoRow(3, 'Status', statusLabel, { badge: true });
    drawInfoRow(4, 'Approved by', approvedBy, { sub: approvedByEmail });

    y = cardTop + cardH + 14;

    // ================= Basic Information =================
    y = this.pdfConfigEnsureSpace(doc, y, 40);
    y = this.pdfConfigSectionHeading(doc, 'Basic Information', y);
    const basicRows: string[][] = [
      ['Contest Name (English)', val(contest?.name)],
      ['Contest Name (Arabic)', val(pick('arabic_name', 'arabicName'))],
      ['Description', stripHtml(pick('details', 'description')) || 'N/A'],
      ['Description (Arabic)', stripHtml(pick('arabic_details', 'arabicDetails')) || 'N/A'],
      ['Contest Type', val(pick('type'))],
      ['Format', val(pick('format_of_contest', 'formatOfContest'))],
      ['Sharing Setting', val(pick('sharingSetting', 'sharing_setting'))],
      ['Gender', val(pick('gender'))],
      ['Age Range', fmtArr(pick('age', 'age_range', 'ageRange', 'age_group', 'ages'))],
      ['Location', fmtArr(pick('location', 'locations'))],
      ['Business Objective', fmtArr(pick('business_objective', 'businessObjective', 'business_objectives'))],
      ['Category', fmtArr(pick('_contest_category', 'contest_category_name', 'category_name', 'contest_category', 'category_id', 'categoryId'))],
      ['UID', val(pick('uid'))],
      ['Access Code', val(pick('access_code', 'accessCode'))],
    ];
    autoTable(doc, {
      startY: y,
      head: [['Field', 'Value']],
      body: basicRows,
      theme: 'striped',
      headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold', textColor: [70, 70, 70] }, 1: { cellWidth: 'auto' } },
      margin: { left: 14, right: 14 },
      ...this.arabicAwareTableHooks(doc, arabicFontLoaded, 118),
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // ================= Contest Setup =================
    y = this.pdfConfigEnsureSpace(doc, y, 40);
    y = this.pdfConfigSectionHeading(doc, 'Contest Setup', y);
    const setupRows: string[][] = [
      ['Number of Winners', val(pick('number_of_winners', 'numberOfWinners'))],
      ['Points Pool per Contest', val(pick('points_pool_per_contest', 'pointsPoolPerContest'))],
      ['Draw Type', val(pick('draw_type', 'drawType'))],
      ['Number of Users', val(pick('no_of_users', 'noOfUsers'))],
      ['Total Levels', String(levelsArr.length || val(pick('totallevels', 'total_levels')))],
      ['Start Date', fmtDate(pick('start_date', 'startDate'))],
      ['End Date', fmtDate(pick('end_date', 'endDate'))],
      ['Prize Announce Date', fmtDate(pick('prize_announce_date', 'prizeAnnounceDate'))],
    ];
    autoTable(doc, {
      startY: y,
      head: [['Field', 'Value']],
      body: setupRows,
      theme: 'striped',
      headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold', textColor: [70, 70, 70] }, 1: { cellWidth: 'auto' } },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // ================= Prizes =================
    const prizes = Array.isArray(contest?._contest_prize) ? contest._contest_prize : [];
    if (prizes.length) {
      y = this.pdfConfigEnsureSpace(doc, y, 30);
      y = this.pdfConfigSectionHeading(doc, 'Prizes', y);

      const prizeRows = prizes.map((p: any, i: number) => {
        const pr = p?.prize ?? p?.prize_details ?? p?.contest_prize ?? p;
        const catRaw = pickLevel(pr, '_categories', 'categories', 'categories_id', 'category_ids', 'category_id', 'categoryId', 'category_type', 'category');
        return [
          String(i + 1),
          val(pickLevel(pr, 'name', 'prize_name', 'arabic_name', 'title')),
          val(pickLevel(pr, 'value', 'prize_value', 'amount', 'prizeAmount')),
          val(this.resolveCategoryNames(catRaw, categoryNameMap)),
        ];
      });
      autoTable(doc, {
        startY: y,
        head: [['#', 'Prize Name', 'Value', 'Category']],
        body: prizeRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 30 }, 3: { cellWidth: 40 } },
        margin: { left: 14, right: 14 },
        ...this.arabicAwareTableHooks(doc, arabicFontLoaded, 94),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ================= Levels =================
    if (levelsArr.length) {
      y = this.pdfConfigEnsureSpace(doc, y, 30);
      y = this.pdfConfigSectionHeading(doc, `Levels (${levelsArr.length})`, y);

      const boolLabel = (v: any) => (v === true || v === 'true' || v === 1 ? 'Yes' : (v === false || v === 'false' || v === 0 ? 'No' : 'N/A'));

      levelsArr.forEach((lvl: any, i: number) => {
        y = this.pdfConfigEnsureSpace(doc, y, 40);

        // Core rows are always shown; optional rows only when they carry a value
        // (so type-specific fields don't flood the report with N/A).
        const rows: string[][] = [
          ['Level Name', val(pickLevel(lvl, 'name', 'level_name'))],
          ['Arabic Name', val(pickLevel(lvl, 'arabic_name', 'arabicName'))],
          ['Type', val(pickLevel(lvl, 'type', 'level_type', 'levelType'))],
          ['Description', stripHtml(pickLevel(lvl, 'description', 'text_content')) || 'N/A'],
          ['Arabic Description', stripHtml(pickLevel(lvl, 'arabic_description', 'arabicDescription')) || 'N/A'],
          ['Start Date', fmtDate(pickLevel(lvl, 'level_start_date', 'start_date', 'level_start'))],
          ['Timeline (End)', fmtDate(pickLevel(lvl, 'level_timeline', 'timeline', 'end_date'))],
        ];

        const addOpt = (label: string, value: any) => {
          if (value !== null && value !== undefined && String(value).trim() !== '') {
            rows.push([label, String(value)]);
          }
        };
        const acceptAny = pickLevel(lvl, 'accept_any_answer');
        if (acceptAny !== undefined) addOpt('Accept Any Answer', boolLabel(acceptAny));
        addOpt('Question Order', pickLevel(lvl, 'question_order'));
        addOpt('Min Answers Required', pickLevel(lvl, 'min_answers_required', 'minimum_answers'));
        addOpt('Client Webhook URL', pickLevel(lvl, 'client_webhook_url'));
        addOpt('Luqta Webhook URL', pickLevel(lvl, 'luqta_webhook_url'));
        addOpt('Link Task URL', pickLevel(lvl, 'link_task_url', 'task_url'));
        addOpt('Link Task Instruction', pickLevel(lvl, 'link_task_instruction', 'task_instruction'));
        addOpt('Link Task Timer', pickLevel(lvl, 'link_task_timer', 'timer_duration'));
        addOpt('Quiz ID', pickLevel(lvl, 'quizzes_id'));

        autoTable(doc, {
          startY: y,
          head: [[`Level ${i + 1}`, '']],
          body: rows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 8.5 },
          columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold', textColor: [70, 70, 70] }, 1: { cellWidth: 'auto' } },
          margin: { left: 14, right: 14 },
          ...this.arabicAwareTableHooks(doc, arabicFontLoaded, 118),
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      });
    }

    // ================= Footer =================
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${p} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
      doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, pageHeight - 6);
    }

    const isAr = isArabicLang();
    const rawContestName = (isAr && contest?.arabic_name?.trim()) ? contest.arabic_name.trim() : (contest?.name || 'contest');
    const safeName = String(rawContestName).replace(/\s+/g, '_').replace(/[^\w\-\u0600-\u06FF]/g, '');
    const timestamp = new Date().toISOString().split('T')[0];
    const configSuffix = isAr ? 'إعدادات' : 'configuration';
    doc.save(`${safeName}_${configSuffix}_${timestamp}.pdf`);
    this.sharedService.createNotification('success', isAr ? 'تم تنزيل ملف إعدادات PDF بنجاح' : 'Configuration PDF downloaded successfully');
  }

  private formatExportDate(timestamp: any): string {
    const isAr = isArabicLang();
    if (!timestamp) return isAr ? 'غير متوفر' : 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return isAr ? 'غير متوفر' : 'N/A';

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return isAr ? 'غير متوفر' : 'N/A';
    }
  }

  private extractContestPrizes(contest: any): any[] {
    if (contest.prize && Array.isArray(contest.prize) && contest.prize.length > 0) {
      return contest.prize;
    }

    if (contest._contest_prize_of_contest_list && Array.isArray(contest._contest_prize_of_contest_list)) {
      return contest._contest_prize_of_contest_list;
    }

    if (contest._contest_prize && Array.isArray(contest._contest_prize)) {
      return contest._contest_prize;
    }

    if (contest.levels && Array.isArray(contest.levels)) {
      const nestedPrizes: any[] = [];
      contest.levels.forEach((level: any) => {
        if (level.prize && Array.isArray(level.prize)) {
          nestedPrizes.push(...level.prize);
        }
      });
      return nestedPrizes;
    }

    return [];
  }

  private getPdfLocationLabel(rawLocation: string): string {
    if (!rawLocation || rawLocation === 'N/A') {
      return 'N/A';
    }

    const countryList = Array.isArray(this.countryRecords) ? this.countryRecords : [];
    if (!countryList.length) {
      return rawLocation;
    }

    const normalizedLocation = rawLocation.toLowerCase();
    const uniqueCountryNames = Array.from(
      new Set(
        countryList
          .map((country: any) => String(country?.name || '').trim())
          .filter((name: string) => !!name)
      )
    );

    if (!uniqueCountryNames.length) {
      return rawLocation;
    }

    const allCountriesIncluded = uniqueCountryNames.every((countryName: string) =>
      normalizedLocation.includes(countryName.toLowerCase())
    );

    if (allCountriesIncluded) {
      return 'All';
    }

    return this.getExcelLocationLabel(rawLocation);
  }

  private getExcelLocationLabel(rawLocation: string): string {
    if (!rawLocation || rawLocation === 'N/A' || rawLocation === '-') {
      return 'N/A';
    }

    const countries = rawLocation
      .split(',')
      .map((country: string) => country.trim())
      .filter((country: string) => !!country);

    if (countries.length <= 5) {
      return rawLocation;
    }

    const firstFive = countries.slice(0, 5).join(', ');
    const remainingCount = countries.length - 5;
    return `${firstFive}, +${remainingCount}`;
  }

  private getArabicLocation(location: string): string {
    if (!location || location === 'N/A' || location === '-') return 'لا يوجد';
    if (location.toLowerCase() === 'all' || location === 'الكل' || location === 'جميع الدول') return 'جميع الدول';

    const parts = location.split(',').map((p: string) => p.trim()).filter(Boolean);
    const translatedParts = parts.map((part: string) => {
      if (part.startsWith('+')) {
        return part;
      }
      return translateCountryName(part, true);
    });

    return translatedParts.join('، ');
  }

  private buildContestExportData(
    contestsToExport: any[],
    collapseAllCountriesToAll: boolean = false,
    limitCountriesForExcel: boolean = false
  ): any[] {
    const isAr = isArabicLang();

    return contestsToExport.map((contest: any) => {
      const allPrizes = this.extractContestPrizes(contest);

      const prizeNames = allPrizes.length > 0
        ? allPrizes
            .map((p: any) => {
              if (isAr) {
                const arName = (p.arabic_name || p.arabicName || p.prize_name_ar || p.name_ar)?.trim();
                if (arName) return arName;
                const engName = p.name || p.prize_name || p.title || '';
                return this.translatePrizeName(engName);
              }
              return p.name || p.prize_name || p.title || '';
            })
            .filter((n: string) => n)
            .join(isAr ? '، ' : ', ') || (isAr ? 'لا يوجد' : 'N/A')
        : (isAr ? 'لا يوجد' : 'N/A');

      const prizeValues = allPrizes.length > 0
        ? allPrizes
          .map((p: any) => p?.value ?? p?.prize_value ?? p?.amount ?? p?.prizeAmount)
          .filter((v: any) => v !== null && v !== undefined && String(v).trim() !== '')
          .map((v: any) => String(v))
          .join(isAr ? '، ' : ', ') || (isAr ? 'لا يوجد' : 'N/A')
        : (isAr ? 'لا يوجد' : 'N/A');

      const levels = contest.levels || contest._levels_of_contest_list || contest.levels_of_contest || [];
      const levelCount = contest.level_count ?? (Array.isArray(levels) ? levels.length : 0);

      const isIntegrated = (contest.format_of_contest || '').toLowerCase() === 'integrated'
        ? (isAr ? 'نعم' : 'Yes')
        : (isAr ? 'لا' : 'No');

      const rawType = (contest.type || contest.contest_type || '').toLowerCase().trim();
      let typeLabel = contest.type || contest.contest_type || '-';
      if (isAr) {
        if (rawType === 'public') typeLabel = 'عام';
        else if (rawType === 'private') typeLabel = 'خاص';
        else if (rawType === 'vip') typeLabel = 'VIP';
        else if (rawType.includes('public') && rawType.includes('vip')) typeLabel = 'عام VIP';
        else if (rawType.includes('private') && rawType.includes('vip')) typeLabel = 'خاص VIP';
      }

      const rawViewType = contest.View_Type || contest.view_type || contest.viewType || 'Normal';
      let viewTypeLabel = rawViewType;
      if (isAr) {
        const vtNorm = String(rawViewType).toLowerCase().trim();
        if (vtNorm.includes('recent')) viewTypeLabel = 'مسابقة حديثة';
        else if (vtNorm === 'normal') viewTypeLabel = 'عادي';
      }

      const rawLocation = contest.location || contest.contest_location || 'N/A';
      let location = collapseAllCountriesToAll
        ? this.getPdfLocationLabel(rawLocation)
        : rawLocation;

      if (limitCountriesForExcel) {
        location = this.getExcelLocationLabel(location);
      }
      if (isAr) {
        location = this.getArabicLocation(location);
      }

      const explicitArName = (contest.arabic_name?.trim() || contest.arabicName?.trim() || contest.name_ar?.trim() || contest.contest_name_ar?.trim() || contest.arabic_title?.trim());
      const contestName = isAr
        ? (explicitArName || this.translateContestName(contest.name || contest.contest_name || 'غير متوفر'))
        : (contest.name || contest.contest_name || 'N/A');

      const contestDesc = (isAr && (contest.arabic_details?.trim() || contest.arabic_description?.trim() || contest.details_ar?.trim() || contest.description_ar?.trim()))
        ? (contest.arabic_details?.trim() || contest.arabic_description?.trim() || contest.details_ar?.trim() || contest.description_ar?.trim())
        : (contest.description || contest.details || (isAr ? 'غير متوفر' : 'N/A'));

      return {
        'ID': contest.id || (isAr ? 'غير متوفر' : 'N/A'),
        'Name': contestName,
        'Start Date': this.formatExportDate(contest.start_date),
        'End Date': this.formatExportDate(contest.end_date),
        'Prize Announce Date': this.formatExportDate(contest.prize_announce_date || contest.prize_announcement_date),
        'Status': this.getStatusLabel(this.getDisplayStatus(contest)),
        'Type': typeLabel,
        'View Type': viewTypeLabel,
        'Number of Levels': levelCount,
        'Number of Participants': contest.number_of_participants ?? contest.participants_count ?? contest.contest_participate ?? 0,
        'Access Code': this.getAccessCodeDisplay(contest) !== '-' ? contest.access_code : (isAr ? 'لا يوجد' : 'N/A'),
        'Location': location,
        'Integrated': isIntegrated,
        'Prize Names': prizeNames,
        'Prize Values': prizeValues,
        'Created At': this.formatExportDate(contest.created_at),
        'Description': contestDesc
      };
    });
  }

  async generateContestPDF(contestsToExport: any[], reportTitle: string, fileName: string) {
    const isAr = isArabicLang();

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const columns = [
      'ID',
      'Name',
      'Start Date',
      'End Date',
      'Prize Announce Date',
      'Status',
      'Type',
      'View Type',
      'Number of Levels',
      'Number of Participants',
      'Access Code',
      'Location',
      'Integrated',
      'Prize Names',
      'Prize Values',
      'Created At',
      'Description',
      'Preview',
    ];
    const columnsAr = [
      'المعرف',
      'الاسم',
      'تاريخ البدء',
      'تاريخ الانتهاء',
      'تاريخ الإعلان عن الجائزة',
      'الحالة',
      'النوع',
      'نوع العرض',
      'عدد المستويات',
      'عدد المشاركين',
      'رمز الدخول',
      'الموقع',
      'متكامل',
      'أسماء الجوائز',
      'قيم الجوائز',
      'تاريخ الإنشاء',
      'الوصف',
      'معاينة',
    ];
    const previewColIndex = columns.length - 1;

    const exportData = this.buildContestExportData(contestsToExport, true);
    // NOTE: `columns` (English keys) must be used for the row-data lookup below;
    // `headColumns` (below) is only the translated label actually rendered as the header.
    const tableRows = exportData.map((row: any) => columns.map((column) => column === 'Preview' ? '' : (row[column] ?? 'N/A')));
    const horizontalMargin = 20; // left 10 + right 10
    const availableTableWidth = pageWidth - horizontalMargin;
    const baseColumnWidths = [8, 18, 14, 14, 16, 22, 10, 12, 10, 14, 12, 18, 10, 14, 12, 14, 23, 16];
    const totalBaseWidth = baseColumnWidths.reduce((sum, width) => sum + width, 0);
    const widthScale = totalBaseWidth > 0 ? availableTableWidth / totalBaseWidth : 1;
    const scaledColumnStyles = baseColumnWidths.reduce((acc: any, width: number, index: number) => {
      acc[index] = { cellWidth: Math.max(7, Number((width * widthScale).toFixed(2))) };
      return acc;
    }, {});

    const shouldLoadArabicFont = isAr || tableRows.some((row: unknown[]) =>
      row.some((cell: unknown) => this.containsArabic(String(cell ?? '')))
    );
    const arabicFontLoaded = shouldLoadArabicFont ? await this.ensureArabicFont(doc) : false;

    // Translated header row actually rendered in the table (English text stays byte-identical).
    const headColumns = isAr
      ? (arabicFontLoaded ? columnsAr.map((label) => reshapeArabic(label)) : columnsAr)
      : columns;
    const logoData = await this.sharedService.getLuqtaLogoDataUrl();
    const clientLogoData = await this.sharedService.getClientLogoDataUrl();
    const previewDataUrls = await Promise.all(
      contestsToExport.map((contest: any) => {
        const bannerUrl = contest?.banners?.[0]?.url || contest?.banners?.[0]?.path || '';
        return bannerUrl ? this.sharedService.getImageDataUrl(bannerUrl) : Promise.resolve(clientLogoData);
      })
    );

    // Generate autoTable
    autoTable(doc, {
      startY: 45,
      head: [headColumns],
      body: tableRows,
      styles: {
        fontSize: 6.5,
        cellPadding: 3,
        lineColor: [224, 224, 224],
        lineWidth: 0.1,
        overflow: 'linebreak',
        minCellHeight: 14, // keeps the Preview thumbnail column legible on short rows
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: 255,
        // autoTable defaults header fontStyle to 'bold', but Amiri is only registered
        // as 'normal' — asking jsPDF for a 'bold' variant that was never added makes it
        // silently fall back to the default font (no Arabic glyphs), producing mojibake.
        fontStyle: (isAr && arabicFontLoaded) ? 'normal' : 'bold',
        halign: isAr ? 'right' : 'center',
        valign: 'middle',
        fontSize: 6.5,
        ...(isAr && arabicFontLoaded ? { font: 'Amiri' } : {}),
      },
      bodyStyles: {
        textColor: [85, 85, 85],
        halign: 'left',
        valign: 'middle',
        fillColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      margin: { top: 45, left: 10, right: 10, bottom: 20 },
      columnStyles: scaledColumnStyles,
      tableLineColor: [224, 224, 224],
      tableLineWidth: 0,
      didParseCell: (data) => {
        if (data.section !== 'body') return;

        // Keep status badge cell untouched in text rendering; it is custom-drawn in didDrawCell.
        if (data.column.index === 5) {
          data.cell.text = [''];
          return;
        }

        const raw = String(data.cell.raw ?? '');
        if (!this.containsArabic(raw)) {
          if (isAr) {
            data.cell.styles.halign = 'center';
          }
          return;
        }

        data.cell.styles.halign = 'right';
        data.cell.styles.fontSize = 7.5;
        if (!arabicFontLoaded) {
          data.cell.text = [''];
          return;
        }

        data.cell.styles.font = 'Amiri';
        data.cell.text = [reshapeArabic(raw)];
      },
      didDrawPage: (data) => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, 42, 'F');

        if (logoData) {
          doc.addImage(logoData, 'PNG', 10, 8, 10, 10);
        }

        const totalEngagementsLabel = isAr ? 'إجمالي المشاركات:' : 'Total Engagements:';
        const generatedDateLabel = isAr ? 'تاريخ الإصدار:' : 'Generated Date:';
        const pageOfLabel = isAr
          ? `الصفحة ${data.pageNumber} من ${doc.internal.pages.length - 1}`
          : `Page ${data.pageNumber} of ${doc.internal.pages.length - 1}`;

        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        this.drawArabicAwareText(doc, reportTitle, pageWidth / 2, 15, arabicFontLoaded, {
          align: 'center',
        });

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        this.drawArabicAwareText(doc, totalEngagementsLabel, 14, 28, arabicFontLoaded);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(85, 85, 85);
        doc.text(tableRows.length.toString(), 50, 28);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        this.drawArabicAwareText(doc, generatedDateLabel, 14, 35, arabicFontLoaded);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(85, 85, 85);
        doc.text(new Date().toLocaleDateString('en-GB'), 50, 35);

        doc.setDrawColor(224, 224, 224);
        doc.setLineWidth(0.5);
        doc.line(14, 40, pageWidth - 14, 40);

        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        this.drawArabicAwareText(doc, pageOfLabel, pageWidth - 14, pageHeight - 10, arabicFontLoaded, {
          align: 'right',
        });
      },
      didDrawCell: (data) => {
        // Fallback path for Arabic text when Unicode font could not be loaded.
        if (!arabicFontLoaded && data.section === 'body' && data.column.index !== 5) {
          const raw = String(data.cell.raw ?? '');
          if (this.containsArabic(raw)) {
            this.drawArabicCellAsImage(doc, raw, data.cell.x, data.cell.y, data.cell.width, data.cell.height);
          }
        }

        // CONTEST PREVIEW (banner thumbnail)
        if (data.section === 'body' && data.column.index === previewColIndex) {
          const previewData = previewDataUrls[data.row.index];
          if (previewData) {
            const padding = 1;
            const imgSize = Math.min(data.cell.width, data.cell.height) - padding * 2;
            const x = data.cell.x + (data.cell.width - imgSize) / 2;
            const y = data.cell.y + (data.cell.height - imgSize) / 2;
            try {
              doc.addImage(previewData, 'JPEG', x, y, imgSize, imgSize);
            } catch {
              // Skip silently if the image data is unusable.
            }
          }
        }

        // STATUS BADGE
        if (data.section === 'body' && data.column.index === 5) {
          const statusValue = String(data.cell.raw ?? '');
          const statusNorm = statusValue.toLowerCase().replace(/\s+/g, '_');

          let bgColor: number[] = [245, 245, 245];
          let textColor: number[] = [85, 85, 85];
          let displayStatusText = statusValue;

          if (statusNorm === 'approved' || statusValue === 'معتمد') {
            bgColor = [212, 237, 218]; textColor = [21, 87, 36];
            displayStatusText = isAr ? 'معتمد' : 'Approved';
          } else if (statusNorm === 'pending' || statusValue === 'قيد الانتظار' || statusValue === 'قيد_الانتظار') {
            bgColor = [255, 236, 179]; textColor = [134, 100, 0];
            displayStatusText = isAr ? 'قيد الانتظار' : 'Pending';
          } else if (statusNorm === 'completed' || statusValue === 'مكتمل') {
            bgColor = [217, 245, 232]; textColor = [16, 122, 78];
            displayStatusText = isAr ? 'مكتمل' : 'Completed';
          } else if (statusNorm === 'draft' || statusValue === 'مسودة') {
            bgColor = [226, 227, 229]; textColor = [56, 61, 65];
            displayStatusText = isAr ? 'مسودة' : 'Draft';
          } else if (statusNorm === 'ongoing' || statusValue === 'نشط') {
            bgColor = [210, 229, 255]; textColor = [22, 95, 168];
            displayStatusText = isAr ? 'نشط' : 'Ongoing';
          } else if (statusNorm === 'waiting_prize' || statusValue === 'في انتظار الجائزة' || statusValue === 'في_انتظار_الجائزة') {
            bgColor = [235, 220, 250]; textColor = [104, 57, 145];
            displayStatusText = isAr ? 'في انتظار الجائزة' : 'Waiting Prize';
          } else if (statusNorm === 'waiting_publish' || statusValue === 'في انتظار النشر') {
            bgColor = [255, 236, 179]; textColor = [134, 100, 0];
            displayStatusText = isAr ? 'في انتظار النشر' : 'Waiting for Publish';
          } else if (statusNorm === 'rejected' || statusValue === 'مرفوض') {
            bgColor = [250, 219, 219]; textColor = [153, 27, 27];
            displayStatusText = isAr ? 'مرفوض' : 'Rejected';
          } else if (statusNorm === 'luqta_pending' || statusValue === 'قيد مراجعة لقطة') {
            bgColor = [255, 231, 217]; textColor = [154, 74, 22];
            displayStatusText = isAr ? 'قيد مراجعة لقطة' : 'Luqta Pending';
          } else if (statusNorm === 'web_approved' || statusValue === 'معتمد للويب') {
            bgColor = [212, 237, 218]; textColor = [21, 87, 36];
            displayStatusText = isAr ? 'معتمد للويب' : 'Web Approved';
          }

          const padding = 2;
          const badgeWidth = data.cell.width - (padding * 2);
          const badgeHeight = 6;
          const badgeX = data.cell.x + padding;
          const badgeY = data.cell.y + (data.cell.height - badgeHeight) / 2;

          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 1.5, 1.5, 'F');

          doc.setTextColor(textColor[0], textColor[1], textColor[2]);
          if (isAr && arabicFontLoaded) {
            doc.setFont('Amiri', 'normal');
            doc.setFontSize(7);
            doc.text(reshapeArabic(displayStatusText), data.cell.x + data.cell.width / 2, badgeY + 4.2, {
              align: 'center'
            });
          } else {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6.5);
            doc.text(displayStatusText, data.cell.x + data.cell.width / 2, badgeY + 4.5, {
              align: 'center'
            });
          }
        }

        // Horizontal lines only
        if (data.section === 'body') {
          doc.setDrawColor(224, 224, 224);
          doc.setLineWidth(0.1);
          doc.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          );
        }
      }
    });

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`${fileName}_${timestamp}.pdf`);
    this.sharedService.createNotification('success', 'PDF downloaded successfully');
  }

  private drawArabicCellAsImage(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    const canvas = document.createElement('canvas');
    const scale = 2;
    const targetWidthPx = 220;
    const targetHeightPx = 64;

    canvas.width = targetWidthPx * scale;
    canvas.height = targetHeightPx * scale;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, targetWidthPx, targetHeightPx);
    ctx.fillStyle = '#000000';
    ctx.direction = 'rtl';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    let fontPx = 28;
    const minFontPx = 16;
    const maxTextWidth = targetWidthPx - 10;

    while (fontPx > minFontPx) {
      ctx.font = `500 ${fontPx}px "Segoe UI", Tahoma, Arial, sans-serif`;
      if (ctx.measureText(text).width <= maxTextWidth) break;
      fontPx -= 1;
    }

    ctx.font = `500 ${fontPx}px "Segoe UI", Tahoma, Arial, sans-serif`;
    ctx.fillText(text, targetWidthPx - 5, targetHeightPx / 2);

    const imgData = canvas.toDataURL('image/png');
    const imgTargetHeight = Math.min(5.2, Math.max(4.0, height - 1.5));
    const imgTargetWidth = Math.min(
      width - 2,
      (targetWidthPx / targetHeightPx) * imgTargetHeight
    );
    const imgX = x + width - imgTargetWidth - 1;
    const imgY = y + (height - imgTargetHeight) / 2;

    doc.addImage(imgData, 'PNG', imgX, imgY, imgTargetWidth, imgTargetHeight, undefined, 'FAST');
  }

  private containsArabic(value: string): boolean {
    return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(value);
  }

  /**
   * Draws a title/heading/label string with doc.text(), switching to this file's
   * loaded 'Amiri' font and reshaping the text when it contains Arabic
   * (e.g. a translated static label, or an Arabic report title). Restores the
   * previously active font afterward so subsequent English text is unaffected.
   */
  private drawArabicAwareText(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    arabicFontLoaded: boolean,
    options?: any
  ): void {
    if (this.containsArabic(text) && arabicFontLoaded) {
      const prevFont = doc.getFont();
      doc.setFont('Amiri', 'normal');
      doc.text(reshapeArabic(text), x, y, options);
      doc.setFont(prevFont.fontName, prevFont.fontStyle);
    } else {
      doc.text(text, x, y, options);
    }
  }

  private async ensureArabicFont(doc: jsPDF): Promise<boolean> {
    try {
      const fontName = 'Amiri';
      const fontFile = 'Amiri-Regular.ttf';
      const fontList = doc.getFontList?.() as Record<string, unknown> | undefined;

      if (fontList && fontList[fontName]) {
        return true;
      }

      // Bundled locally (assets/fonts) rather than fetched from an external
      // GitHub URL — that fetch was flaky in production (CORS/network/rate-limit),
      // which silently fell back to the default font and produced mojibake for
      // any Arabic report title/header. Same font already used by arabic-pdf.util.ts.
      const response = await fetch('assets/fonts/Amiri-Regular.ttf');
      if (!response.ok) return false;

      const fontBuffer = await response.arrayBuffer();
      const fontBase64 = this.arrayBufferToBase64(fontBuffer);

      doc.addFileToVFS(fontFile, fontBase64);
      doc.addFont(fontFile, fontName, 'normal');
      return true;
    } catch {
      return false;
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  }


  // Excel Export ke liye complete function

// Excel Download Main Function
downloadExcel() {
  const exportConfig = this.getExportConfigByTab();
  this.fetchAllContestsForExport(exportConfig.status, (contestsToExport) => {
    this.generateContestExcel(contestsToExport, exportConfig.fileName);
  });
}

// Excel Generate Function with Date Formatting
generateContestExcel(contestsToExport: any[], fileName: string) {
  const isAr = isArabicLang();
  const rawData = this.buildContestExportData(contestsToExport, false, true);

  const excelData = isAr ? rawData.map((row: any) => ({
    'المعرف': row['ID'],
    'الاسم': row['Name'],
    'تاريخ البدء': row['Start Date'],
    'تاريخ الانتهاء': row['End Date'],
    'تاريخ الإعلان عن الجائزة': row['Prize Announce Date'],
    'الحالة': row['Status'],
    'النوع': row['Type'],
    'نوع العرض': row['View Type'],
    'عدد المستويات': row['Number of Levels'],
    'عدد المشاركين': row['Number of Participants'],
    'رمز الدخول': row['Access Code'],
    'الموقع': row['Location'],
    'متكامل': row['Integrated'],
    'أسماء الجوائز': row['Prize Names'],
    'قيم الجوائز': row['Prize Values'],
    'تاريخ الإنشاء': row['Created At'],
    'الوصف': row['Description']
  })) : rawData;

  // Worksheet banao
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Set RTL direction if Arabic
  if (isAr) {
    worksheet['!views'] = [{ RTL: true }];
  }

  // Column widths set karo
  worksheet['!cols'] = [
    { wch: 10 },  // ID / المعرف
    { wch: 30 },  // Name / الاسم
    { wch: 20 },  // Start Date / تاريخ البدء
    { wch: 20 },  // End Date / تاريخ الانتهاء
    { wch: 24 },  // Prize Announce Date / تاريخ الإعلان عن الجائزة
    { wch: 18 },  // Status / الحالة
    { wch: 15 },  // Type / النوع
    { wch: 18 },  // View Type / نوع العرض
    { wch: 15 },  // Number of Levels / عدد المستويات
    { wch: 20 },  // Number of Participants / عدد المشاركين
    { wch: 15 },  // Access Code / رمز الدخول
    { wch: 30 },  // Location / الموقع
    { wch: 12 },  // Integrated / متكامل
    { wch: 30 },  // Prize Names / أسماء الجوائز
    { wch: 20 },  // Prize Values / قيم الجوائز
    { wch: 20 },  // Created At / تاريخ الإنشاء
    { wch: 40 }   // Description / الوصف
  ];

  // Workbook banao
  const workbook = XLSX.utils.book_new();
  const sheetName = isAr ? 'المسابقات' : 'Engagements';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Excel file ko array buffer mein convert karo
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
  // Blob banao
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });

  // File name with timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  
  // File save karo
  saveAs(blob, `${fileName}_${timestamp}.xlsx`);
  
  // Success notification
  this.sharedService.createNotification('success', isAr ? 'تم تنزيل ملف Excel بنجاح' : 'Excel downloaded successfully');
}

  // Date format karne ka helper function
  formatDate(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format: DD/MM/YYYY HH:mm
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  }

  // Alternative: Agar sirf date chahiye (without time)
  formatDateOnly(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date';

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  // Image Modal Methods
  openImageModal(images: any[], startIndex: number = 0): void {
    // Convert banner objects to URL strings
    this.imageModalImages = images
      .filter(img => img?.url)
      .map(img => img.url);

    if (this.imageModalImages.length > 0) {
      this.imageModalCurrentIndex = startIndex;
      this.imageModalVisible = true;
    }
  }

  openSingleImage(imageUrl: string): void {
    if (imageUrl) {
      this.imageModalImages = [imageUrl];
      this.imageModalCurrentIndex = 0;
      this.imageModalVisible = true;
    }
  }

  closeImageModal(): void {
    this.imageModalVisible = false;
    this.imageModalImages = [];
    this.imageModalCurrentIndex = 0;
  }

  onImageIndexChange(index: number): void {
    this.imageModalCurrentIndex = index;
  }
}
